/**
 * Profile - Comprehensive Player Profile & Games Played Records Manager
 * Contains player name, avatar persona, career match statistics, and full match history.
 */

import { AvatarManager } from '../ui/AvatarManager.js';
import { appStorage } from '../storage/IndexedDBStorage.js';
import { BADGE_DEFINITIONS } from '../achievements/AchievementManager.js';
import { sanitizeUserInput } from '../utils/Sanitizer.js';

function getStorage(storage = null) {
  return storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
}

export const STORAGE_KEY_PROFILE = 'cc_player_profile';
export const STORAGE_KEY_NAME = 'cc_player_name';
export const STORAGE_KEY_AVATAR = 'cc_player_avatar';
export const STORAGE_KEY_DAILY_PUZZLE = 'cc_daily_puzzle_stats';
export const STORAGE_KEY_DISCARD_TRAINER = 'cc_discard_trainer_stats';
export const STORAGE_KEY_PEGGING_TRAINER = 'cc_pegging_trainer_stats';
export const STORAGE_KEY_COUNTING_TRAINER = 'cc_counting_trainer_stats';
export const STORAGE_KEY_CUSTOM_3D_BOARDS = 'cc_custom_3d_boards';

export const GAME_BRANDS = Object.freeze([
  { id: 'crazy', name: 'Crazy Cribbage', shortName: 'Crazy', icon: '🃏', color: '#facc15', badgeBg: 'rgba(245, 158, 11, 0.2)', border: 'rgba(245, 158, 11, 0.4)' },
  { id: 'clash', name: 'Cribbage Clash', shortName: 'Clash', icon: '⚔️', color: '#38bdf8', badgeBg: 'rgba(56, 189, 248, 0.2)', border: 'rgba(56, 189, 248, 0.4)' },
  { id: 'pints', name: 'Pints & Pegs', shortName: 'Pints', icon: '🍺', color: '#10b981', badgeBg: 'rgba(16, 185, 129, 0.2)', border: 'rgba(16, 185, 129, 0.4)' },
  { id: 'adult', name: 'Skunked & Stripped', shortName: 'Adult', icon: '🦨', color: '#ec4899', badgeBg: 'rgba(244, 63, 94, 0.2)', border: 'rgba(244, 63, 94, 0.4)' }
]);

export class Profile {
  static _instance = null;

  constructor(options = {}) {
    this.name = options.name || 'Player';
    this.avatar = options.avatar || 'joker';
    this.stats = {
      totalGamesPlayed: 0,
      wins: 0,
      losses: 0,
      currentStreak: 0,
      bestStreak: 0,
      skunkWins: 0,
      skunkLosses: 0,
      highestScore: 0,
      totalPoints: 0,
      lastPlayed: null,
      dailyPuzzle: {
        currentStreak: 0,
        bestStreak: 0,
        lastPlayedDate: '',
        history: {},
        totalPlayed: 0,
        perfectDiscards: 0,
        peggingWins: 0,
        ...(options.stats?.dailyPuzzle || {})
      },
      discardTrainer: {
        handsPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        accuracyPct: 0,
        lastPlayedDate: '',
        ...(options.stats?.discardTrainer || {})
      },
      peggingTrainer: {
        scenariosPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        accuracyPct: 0,
        lastPlayedDate: '',
        ...(options.stats?.peggingTrainer || {})
      },
      countingTrainer: {
        handsPlayed: 0,
        handsCorrect: 0,
        handsWrong: 0,
        currentStreak: 0,
        bestStreak: 0,
        accuracyPct: 0,
        lastPlayedDate: '',
        ...(options.stats?.countingTrainer || {})
      },
      ...(options.stats || {})
    };
    this.history = Array.isArray(options.history) ? options.history : [];
    this.trophies = {
      unlocked: {},
      byBrand: {
        crazy: [],
        clash: [],
        pints: [],
        adult: []
      },
      ...(options.trophies || {})
    };
    if (!this.trophies.byBrand) {
      this.trophies.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(this.trophies.byBrand[b.id])) {
        this.trophies.byBrand[b.id] = [];
      }
    }
    this.replays = {
      byBrand: {
        crazy: [],
        clash: [],
        pints: [],
        adult: []
      },
      list: [],
      ...(options.replays || {})
    };
    if (!this.replays.byBrand) {
      this.replays.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(this.replays.byBrand[b.id])) {
        this.replays.byBrand[b.id] = [];
      }
    }
    if (!Array.isArray(this.replays.list)) {
      this.replays.list = [];
    }
    this.custom3DBoards = {
      byBrand: {
        crazy: [],
        clash: [],
        pints: [],
        adult: []
      },
      list: [],
      ...(options.custom3DBoards || {})
    };
    if (!this.custom3DBoards.byBrand) {
      this.custom3DBoards = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(this.custom3DBoards.byBrand[b.id])) {
        this.custom3DBoards.byBrand[b.id] = [];
      }
    }
    if (!Array.isArray(this.custom3DBoards.list)) {
      this.custom3DBoards.list = [];
    }
  }

  /**
   * Retrieves singleton Profile instance, auto-loading from storage.
   * @param {Object} [storage=null]
   * @returns {Profile}
   */
  static getInstance(storage = null) {
    if (!Profile._instance) {
      Profile._instance = Profile.load(storage);
    }
    return Profile._instance;
  }

  /**
   * Loads player profile data from storage.
   * @param {Object} [storage=null]
   * @returns {Profile}
   */
  static load(storage = null) {
    const s = getStorage(storage);
    let savedName = 'Player';
    let savedAvatar = 'joker';
    let savedData = null;
    let savedDaily = null;
    let savedTrainer = null;
    let savedPeggingTrainer = null;

    if (s) {
      try {
        const rawName = s.getItem(STORAGE_KEY_NAME);
        if (rawName && typeof rawName === 'string') savedName = rawName.trim();

        const rawAvatar = s.getItem(STORAGE_KEY_AVATAR);
        if (rawAvatar && typeof rawAvatar === 'string') savedAvatar = rawAvatar.trim();

        const rawProfile = s.getItem(STORAGE_KEY_PROFILE);
        if (rawProfile && typeof rawProfile === 'string') {
          savedData = JSON.parse(rawProfile);
        }

        const rawDaily = s.getItem(STORAGE_KEY_DAILY_PUZZLE);
        if (rawDaily && typeof rawDaily === 'string') {
          try {
            savedDaily = JSON.parse(rawDaily);
          } catch (_) {}
        }

        const rawTrainer = s.getItem(STORAGE_KEY_DISCARD_TRAINER);
        if (rawTrainer && typeof rawTrainer === 'string') {
          try {
            savedTrainer = JSON.parse(rawTrainer);
          } catch (_) {}
        }

        const rawPegTrainer = s.getItem(STORAGE_KEY_PEGGING_TRAINER);
        if (rawPegTrainer && typeof rawPegTrainer === 'string') {
          try {
            savedPeggingTrainer = JSON.parse(rawPegTrainer);
          } catch (_) {}
        }
      } catch (err) {
        console.warn('[Profile] Failed to load profile from storage:', err);
      }
    }

    if (typeof document !== 'undefined') {
      const optNameEl = document.getElementById('optPlayerName');
      if (optNameEl) {
        if (optNameEl.value && optNameEl.value.trim()) {
          savedName = optNameEl.value.trim();
        } else if (savedName) {
          optNameEl.value = savedName;
        }
      }
    }

    const mergedDaily = {
      currentStreak: 0,
      bestStreak: 0,
      lastPlayedDate: '',
      history: {},
      totalPlayed: 0,
      perfectDiscards: 0,
      peggingWins: 0,
      ...(savedData?.stats?.dailyPuzzle || {}),
      ...(savedDaily || {})
    };

    const mergedTrainer = {
      handsPlayed: 0,
      optimalCount: 0,
      currentStreak: 0,
      bestStreak: 0,
      totalLossEV: 0,
      averageLossEV: 0,
      accuracyPct: 0,
      lastPlayedDate: '',
      ...(savedData?.stats?.discardTrainer || {}),
      ...(savedTrainer || {})
    };
    if (mergedTrainer.handsPlayed > 0) {
      mergedTrainer.accuracyPct = Math.round((mergedTrainer.optimalCount / mergedTrainer.handsPlayed) * 100);
      mergedTrainer.averageLossEV = Math.round((mergedTrainer.totalLossEV / mergedTrainer.handsPlayed) * 100) / 100;
    }

    const mergedPeggingTrainer = {
      scenariosPlayed: 0,
      optimalCount: 0,
      currentStreak: 0,
      bestStreak: 0,
      totalLossEV: 0,
      averageLossEV: 0,
      accuracyPct: 0,
      lastPlayedDate: '',
      ...(savedData?.stats?.peggingTrainer || {}),
      ...(savedPeggingTrainer || {})
    };
    if (mergedPeggingTrainer.scenariosPlayed > 0) {
      mergedPeggingTrainer.accuracyPct = Math.round((mergedPeggingTrainer.optimalCount / mergedPeggingTrainer.scenariosPlayed) * 100);
      mergedPeggingTrainer.averageLossEV = Math.round((mergedPeggingTrainer.totalLossEV / mergedPeggingTrainer.scenariosPlayed) * 100) / 100;
    }

    const finalStats = {
      ...(savedData?.stats || {}),
      dailyPuzzle: mergedDaily,
      discardTrainer: mergedTrainer,
      peggingTrainer: mergedPeggingTrainer
    };

    const savedTrophies = {
      unlocked: {},
      byBrand: { crazy: [], clash: [], pints: [], adult: [] },
      ...(savedData?.trophies || {})
    };
    if (!savedTrophies.byBrand) {
      savedTrophies.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(savedTrophies.byBrand[b.id])) {
        savedTrophies.byBrand[b.id] = [];
      }
    }

    // Two-way sync with crazyCribbage.achievements storage
    if (s) {
      try {
        const rawAch = s.getItem('crazyCribbage.achievements');
        if (rawAch) {
          const parsedAch = JSON.parse(rawAch);
          for (const [badgeId, badgeMeta] of Object.entries(parsedAch)) {
            const bDef = Array.isArray(BADGE_DEFINITIONS) ? BADGE_DEFINITIONS.find(b => b.id === badgeId) : null;
            const brand = bDef?.brand || 'crazy';
            if (!savedTrophies.unlocked[badgeId]) {
              savedTrophies.unlocked[badgeId] = {
                unlockedAt: badgeMeta.unlockedAt || Date.now(),
                brand
              };
            }
            if (!savedTrophies.byBrand[brand].includes(badgeId)) {
              savedTrophies.byBrand[brand].push(badgeId);
            }
          }
        }
      } catch (_) {}
    }

    const savedReplays = {
      byBrand: { crazy: [], clash: [], pints: [], adult: [] },
      list: [],
      ...(savedData?.replays || {})
    };
    if (!savedReplays.byBrand) {
      savedReplays.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(savedReplays.byBrand[b.id])) {
        savedReplays.byBrand[b.id] = [];
      }
    }
    if (!Array.isArray(savedReplays.list)) {
      savedReplays.list = [];
    }

    // Two-way sync with crazyCribbage.replays.v1 storage
    if (s) {
      try {
        const rawRep = s.getItem('crazyCribbage.replays.v1');
        if (rawRep) {
          const parsedRep = JSON.parse(rawRep);
          if (Array.isArray(parsedRep)) {
            for (const r of parsedRep) {
              if (!r || !r.id) continue;
              const brand = r.brand || 'crazy';
              if (!savedReplays.list.some(item => item.id === r.id)) {
                savedReplays.list.push(r);
              }
              if (!savedReplays.byBrand[brand]) {
                savedReplays.byBrand[brand] = [];
              }
              if (!savedReplays.byBrand[brand].some(item => item.id === r.id)) {
                savedReplays.byBrand[brand].push(r);
              }
            }
          }
        }
      } catch (_) {}
    }

    // Sort newest first
    savedReplays.list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    for (const b of GAME_BRANDS) {
      savedReplays.byBrand[b.id].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    }

    const savedCustomBoards = {
      byBrand: { crazy: [], clash: [], pints: [], adult: [] },
      list: [],
      ...(savedData?.custom3DBoards || {})
    };
    if (!savedCustomBoards.byBrand) {
      savedCustomBoards.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(savedCustomBoards.byBrand[b.id])) {
        savedCustomBoards.byBrand[b.id] = [];
      }
    }
    if (!Array.isArray(savedCustomBoards.list)) {
      savedCustomBoards.list = [];
    }

    // Two-way sync with cc_custom_3d_boards storage
    if (s) {
      try {
        const rawBoards = s.getItem(STORAGE_KEY_CUSTOM_3D_BOARDS);
        if (rawBoards) {
          const parsedBoards = JSON.parse(rawBoards);
          if (Array.isArray(parsedBoards)) {
            for (const b of parsedBoards) {
              if (!b || !b.id) continue;
              const brandVal = b.brand || 'crazy';
              const existingIdx = savedCustomBoards.list.findIndex(x => x.id === b.id);
              if (existingIdx >= 0) {
                savedCustomBoards.list[existingIdx] = Object.assign({}, savedCustomBoards.list[existingIdx], b);
              } else {
                savedCustomBoards.list.push(b);
              }
              const targetBrands = (brandVal === 'all')
                ? GAME_BRANDS.map(g => g.id)
                : brandVal.split(',').map(str => str.trim());
              for (const tb of targetBrands) {
                if (savedCustomBoards.byBrand[tb] && !savedCustomBoards.byBrand[tb].some(x => x.id === b.id)) {
                  savedCustomBoards.byBrand[tb].push(b);
                }
              }
            }
          }
        }
      } catch (_) {}
    }

    return new Profile({
      name: savedName,
      avatar: savedAvatar,
      stats: finalStats,
      trophies: savedTrophies,
      replays: savedReplays,
      custom3DBoards: savedCustomBoards,
      history: savedData?.history || []
    });
  }

  /**
   * Saves current profile data to storage.
   * @param {Object} [storage=null]
   */
  save(storage = null) {
    const s = getStorage(storage);
    if (!s) return;

    try {
      if (this.name) s.setItem(STORAGE_KEY_NAME, this.name);
      if (this.avatar) s.setItem(STORAGE_KEY_AVATAR, this.avatar);

      if (this.stats?.dailyPuzzle) {
        try {
          s.setItem(STORAGE_KEY_DAILY_PUZZLE, JSON.stringify(this.stats.dailyPuzzle));
        } catch (_) {}
      }

      if (this.stats?.discardTrainer) {
        try {
          s.setItem(STORAGE_KEY_DISCARD_TRAINER, JSON.stringify(this.stats.discardTrainer));
        } catch (_) {}
      }

      if (this.stats?.peggingTrainer) {
        try {
          s.setItem(STORAGE_KEY_PEGGING_TRAINER, JSON.stringify(this.stats.peggingTrainer));
        } catch (_) {}
      }

      const payload = {
        name: this.name,
        avatar: this.avatar,
        stats: this.stats,
        trophies: this.trophies,
        replays: this.replays,
        custom3DBoards: this.custom3DBoards,
        history: this.history.slice(0, 100), // Cap history to 100 most recent
        updatedAt: Date.now()
      };
      s.setItem(STORAGE_KEY_PROFILE, JSON.stringify(payload));

      if (this.trophies?.unlocked) {
        try {
          s.setItem('crazyCribbage.achievements', JSON.stringify(this.trophies.unlocked));
        } catch (_) {}
      }

      if (this.replays?.list && Array.isArray(this.replays.list)) {
        try {
          s.setItem('crazyCribbage.replays.v1', JSON.stringify(this.replays.list.slice(0, 30)));
        } catch (_) {}
      }

      if (this.custom3DBoards?.list && Array.isArray(this.custom3DBoards.list)) {
        try {
          s.setItem(STORAGE_KEY_CUSTOM_3D_BOARDS, JSON.stringify(this.custom3DBoards.list));
        } catch (_) {}
      }
    } catch (err) {
      console.warn('[Profile] Failed to save profile to storage:', err);
    }
  }

  /**
   * Gets current player name.
   * @param {Object} [storage=null]
   * @returns {string}
   */
  getName(storage = null) {
    const s = getStorage(storage);
    if (s) {
      try {
        const stored = s.getItem(STORAGE_KEY_NAME);
        if (stored && typeof stored === 'string' && stored.trim()) {
          this.name = stored.trim();
          if (typeof document !== 'undefined') {
            const el = document.getElementById('optPlayerName');
            if (el && (!el.value || !el.value.trim())) {
              el.value = this.name;
            }
          }
          return this.name;
        }
      } catch (_) {}
    }
    if (typeof document !== 'undefined') {
      const el = document.getElementById('optPlayerName');
      if (el && el.value.trim()) {
        this.name = el.value.trim();
        return this.name;
      }
    }
    return this.name || 'Player';
  }

  /**
   * Updates player name, syncs with storage and DOM, and triggers update events.
   * @param {string} newName
   * @param {Object} [storage=null]
   * @returns {string}
   */
  setName(newName, storage = null) {
    const sanitized = sanitizeUserInput(newName);
    const alphanumeric = sanitized.replace(/[^a-zA-Z0-9]/g, '').trim();
    this.name = alphanumeric ? alphanumeric.slice(0, 24) : 'Player';

    const s = getStorage(storage);
    if (s) {
      try {
        s.setItem(STORAGE_KEY_NAME, this.name);
      } catch (_) {}
    }

    if (typeof document !== 'undefined') {
      const el = document.getElementById('optPlayerName');
      if (el && el.value !== this.name) {
        el.value = this.name;
      }
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.name;
  }

  /**
   * Gets current avatar ID.
   * @param {Object} [storage=null]
   * @returns {string}
   */
  getAvatar(storage = null) {
    const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
    if (avMgr && typeof avMgr.getSelectedAvatarId === 'function') {
      this.avatar = avMgr.getSelectedAvatarId(getStorage(storage));
    }
    return this.avatar || 'joker';
  }

  /**
   * Sets current player avatar ID.
   * @param {string} avatarId
   * @param {Object} [storage=null]
   * @returns {string}
   */
  setAvatar(avatarId, storage = null) {
    const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
    if (avMgr && typeof avMgr.setSelectedAvatarId === 'function') {
      avMgr.setSelectedAvatarId(avatarId, getStorage(storage));
    }
    this.avatar = avatarId;
    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.avatar;
  }

  /**
   * Returns the full avatar definition object.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getAvatarObject(storage = null) {
    const avId = this.getAvatar(storage);
    const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
    return avMgr ? avMgr.getAvatarById(avId, getStorage(storage)) : null;
  }

  /**
   * Returns career match statistics and win rate.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getStats(storage = null) {
    const total = this.stats.totalGamesPlayed || 0;
    const wins = this.stats.wins || 0;
    const losses = this.stats.losses || 0;
    const winRateNum = total > 0 ? (wins / total) * 100 : 0;
    const winRate = total > 0 ? `${winRateNum.toFixed(1)}%` : '0%';

    return {
      totalGamesPlayed: total,
      wins,
      losses,
      winRate,
      winRateNum: Math.round(winRateNum * 10) / 10,
      currentStreak: this.stats.currentStreak || 0,
      bestStreak: this.stats.bestStreak || 0,
      skunkWins: this.stats.skunkWins || 0,
      skunkLosses: this.stats.skunkLosses || 0,
      highestScore: this.stats.highestScore || 0,
      totalPoints: this.stats.totalPoints || 0,
      lastPlayed: this.stats.lastPlayed || null
    };
  }

  /**
   * Retrieves win/loss statistics and variant setups for all 4 games.
   * @param {Object} [storage=null]
   * @returns {Object} map of brandId -> brand record details
   */
  getGameRecords(storage = null) {
    const s = getStorage(storage);
    const settingsMgr = (typeof window !== 'undefined' && window.CrazyCribbage?.SettingsManager) || null;
    const records = {};

    for (const b of GAME_BRANDS) {
      let wins = 0;
      let losses = 0;
      let variants = [];

      // 1. Pull from SettingsManager variant records if available
      if (settingsMgr && typeof settingsMgr.loadRecords === 'function') {
        const vMap = settingsMgr.loadRecords(s, b.id);
        if (vMap && typeof vMap === 'object') {
          variants = Object.keys(vMap).map(k => ({
            key: k,
            label: vMap[k].label || k,
            wins: Number(vMap[k].wins) || 0,
            losses: Number(vMap[k].losses) || 0
          })).filter(r => r.wins + r.losses > 0)
            .sort((a, b) => (b.wins + b.losses) - (a.wins + a.losses));

          wins = variants.reduce((sum, r) => sum + r.wins, 0);
          losses = variants.reduce((sum, r) => sum + r.losses, 0);
        }
      }

      // 2. Also check match history for this brand
      const brandHistory = (this.history || []).filter(m => (m.brand || 'crazy') === b.id);
      const histWins = brandHistory.filter(m => m.userWon).length;
      const histLosses = brandHistory.filter(m => !m.userWon).length;

      // 3. Check internal stats byBrand
      if (this.stats?.byBrand?.[b.id]) {
        const sb = this.stats.byBrand[b.id];
        wins = Math.max(wins, Number(sb.wins) || 0);
        losses = Math.max(losses, Number(sb.losses) || 0);
      }

      // Ensure we don't under-report if history has entries
      wins = Math.max(wins, histWins);
      losses = Math.max(losses, histLosses);

      const total = wins + losses;
      const winRateNum = total > 0 ? (wins / total) * 100 : 0;
      const winRate = total > 0 ? `${winRateNum.toFixed(1)}%` : '0%';

      const brandTrophies = this.getTrophiesByBrand(b.id, storage);
      const brandReplays = this.getReplaysByBrand(b.id, storage);
      const brandCustomBoards = this.getCustom3DBoardsByBrand(b.id, storage);
      records[b.id] = {
        ...b,
        wins,
        losses,
        total,
        winRate,
        winRateNum: Math.round(winRateNum * 10) / 10,
        trophiesUnlocked: brandTrophies.unlockedCount,
        trophiesTotal: brandTrophies.totalCount,
        trophiesPct: brandTrophies.pct,
        replaysCount: Array.isArray(brandReplays) ? brandReplays.length : 0,
        customBoardsCount: Array.isArray(brandCustomBoards) ? brandCustomBoards.length : 0,
        variants,
        history: brandHistory
      };
    }

    return records;
  }

  /**
   * Computes career totals across all 4 games.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getOverallStats(storage = null) {
    const gameRecs = this.getGameRecords(storage);
    let totalWins = 0;
    let totalLosses = 0;

    for (const b of GAME_BRANDS) {
      totalWins += gameRecs[b.id].wins;
      totalLosses += gameRecs[b.id].losses;
    }

    const totalMatches = Math.max(this.stats.totalGamesPlayed || 0, totalWins + totalLosses);
    const winRateNum = totalMatches > 0 ? (totalWins / totalMatches) * 100 : 0;
    const winRate = totalMatches > 0 ? `${winRateNum.toFixed(1)}%` : '0%';
    const dailyPuzzle = this.getDailyPuzzleStats(storage);
    const discardTrainer = this.getDiscardTrainerStats(storage);
    const peggingTrainer = this.getPeggingTrainerStats(storage);
    const overallTrophies = this.getTrophiesByBrand('all', storage);
    const overallReplays = this.getReplaysByBrand('all', storage);
    const overallCustomBoards = this.getCustom3DBoardsByBrand('all', storage);

    return {
      totalGamesPlayed: totalMatches,
      wins: totalWins,
      losses: totalLosses,
      winRate,
      winRateNum: Math.round(winRateNum * 10) / 10,
      currentStreak: this.stats.currentStreak || 0,
      bestStreak: this.stats.bestStreak || 0,
      skunkWins: this.stats.skunkWins || 0,
      skunkLosses: this.stats.skunkLosses || 0,
      highestScore: this.stats.highestScore || 0,
      totalPoints: this.stats.totalPoints || 0,
      dailyPuzzle,
      discardTrainer,
      peggingTrainer,
      trophies: overallTrophies,
      trophiesUnlocked: overallTrophies.unlockedCount,
      trophiesTotal: overallTrophies.totalCount,
      trophiesPct: overallTrophies.pct,
      replaysTotal: Array.isArray(overallReplays) ? overallReplays.length : 0,
      customBoardsTotal: Array.isArray(overallCustomBoards) ? overallCustomBoards.length : 0
    };
  }

  /**
   * Retrieves player trophies, syncing with crazyCribbage.achievements storage.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getTrophies(storage = null) {
    const s = getStorage(storage);
    if (!this.trophies) {
      this.trophies = { unlocked: {}, byBrand: { crazy: [], clash: [], pints: [], adult: [] } };
    }
    if (!this.trophies.unlocked) this.trophies.unlocked = {};
    if (!this.trophies.byBrand) this.trophies.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(this.trophies.byBrand[b.id])) {
        this.trophies.byBrand[b.id] = [];
      }
    }

    if (s) {
      try {
        const rawAch = s.getItem('crazyCribbage.achievements');
        if (rawAch) {
          const parsed = JSON.parse(rawAch);
          for (const [id, meta] of Object.entries(parsed)) {
            const bDef = Array.isArray(BADGE_DEFINITIONS) ? BADGE_DEFINITIONS.find(b => b.id === id) : null;
            const brand = bDef?.brand || 'crazy';
            if (!this.trophies.unlocked[id]) {
              this.trophies.unlocked[id] = { unlockedAt: meta.unlockedAt || Date.now(), brand };
            }
            if (!this.trophies.byBrand[brand].includes(id)) {
              this.trophies.byBrand[brand].push(id);
            }
          }
        }
      } catch (_) {}
    }
    return this.trophies;
  }

  /**
   * Retrieves trophies separated for a specific brand or all brands.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getTrophiesByBrand(brand = 'all', storage = null) {
    this.getTrophies(storage);
    const allDefs = Array.isArray(BADGE_DEFINITIONS) ? BADGE_DEFINITIONS : [];
    const targetBadges = (!brand || brand === 'all')
      ? allDefs
      : allDefs.filter(b => (b.brand || 'crazy') === brand);

    const unlocked = targetBadges.filter(b => Boolean(this.trophies.unlocked[b.id])).map(b => ({
      ...b,
      unlocked: true,
      unlockedAt: this.trophies.unlocked[b.id]?.unlockedAt || null
    }));

    const locked = targetBadges.filter(b => !this.trophies.unlocked[b.id]).map(b => ({
      ...b,
      unlocked: false,
      unlockedAt: null
    }));

    const totalCount = targetBadges.length;
    const unlockedCount = unlocked.length;
    const pct = totalCount > 0 ? Math.round((unlockedCount / totalCount) * 100) : 0;

    return {
      brand: brand || 'all',
      unlockedCount,
      totalCount,
      pct,
      unlocked,
      locked,
      all: [...unlocked, ...locked]
    };
  }

  /**
   * Records a newly unlocked trophy into player profile under the appropriate brand.
   * @param {string} badgeId
   * @param {string} [brand=null]
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  recordTrophy(badgeId, brand = null, storage = null) {
    if (!badgeId) return this.trophies;
    const bDef = Array.isArray(BADGE_DEFINITIONS) ? BADGE_DEFINITIONS.find(b => b.id === badgeId) : null;
    const resolvedBrand = brand || bDef?.brand || 'crazy';

    if (!this.trophies) {
      this.trophies = { unlocked: {}, byBrand: { crazy: [], clash: [], pints: [], adult: [] } };
    }
    if (!this.trophies.unlocked) this.trophies.unlocked = {};
    if (!this.trophies.byBrand) this.trophies.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    if (!Array.isArray(this.trophies.byBrand[resolvedBrand])) {
      this.trophies.byBrand[resolvedBrand] = [];
    }

    if (!this.trophies.unlocked[badgeId]) {
      this.trophies.unlocked[badgeId] = {
        unlockedAt: Date.now(),
        brand: resolvedBrand
      };
    }
    if (!this.trophies.byBrand[resolvedBrand].includes(badgeId)) {
      this.trophies.byBrand[resolvedBrand].push(badgeId);
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.trophies;
  }

  /**
   * Bulk synchronizes unlocked achievements map into profile trophies.
   * @param {Object} unlockedMap
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  syncTrophiesFromUnlocked(unlockedMap = {}, storage = null) {
    if (!unlockedMap || typeof unlockedMap !== 'object') return this.trophies;
    if (!this.trophies) {
      this.trophies = { unlocked: {}, byBrand: { crazy: [], clash: [], pints: [], adult: [] } };
    }
    if (!this.trophies.unlocked) this.trophies.unlocked = {};
    if (!this.trophies.byBrand) this.trophies.byBrand = { crazy: [], clash: [], pints: [], adult: [] };

    let changed = false;
    for (const [id, meta] of Object.entries(unlockedMap)) {
      const bDef = Array.isArray(BADGE_DEFINITIONS) ? BADGE_DEFINITIONS.find(b => b.id === id) : null;
      const brand = bDef?.brand || 'crazy';
      if (!this.trophies.byBrand[brand]) this.trophies.byBrand[brand] = [];

      if (!this.trophies.unlocked[id]) {
        this.trophies.unlocked[id] = {
          unlockedAt: meta?.unlockedAt || Date.now(),
          brand
        };
        changed = true;
      }
      if (!this.trophies.byBrand[brand].includes(id)) {
        this.trophies.byBrand[brand].push(id);
        changed = true;
      }
    }

    if (changed) {
      this.save(storage);
      Profile.notifyProfileUpdate();
    }
    return this.trophies;
  }

  /**
   * Resets trophies for a specific brand or all brands.
   * @param {Object} [storage=null]
   * @param {string} [brand=null]
   */
  clearTrophies(storage = null, brand = null) {
    const s = getStorage(storage);
    if (!this.trophies) {
      this.trophies = { unlocked: {}, byBrand: { crazy: [], clash: [], pints: [], adult: [] } };
    }

    if (!brand || brand === 'all') {
      this.trophies = {
        unlocked: {},
        byBrand: { crazy: [], clash: [], pints: [], adult: [] }
      };
      if (s) {
        try {
          s.setItem('crazyCribbage.achievements', JSON.stringify({}));
        } catch (_) {}
      }
    } else {
      const toRemove = this.trophies.byBrand[brand] || [];
      for (const id of toRemove) {
        delete this.trophies.unlocked[id];
      }
      this.trophies.byBrand[brand] = [];
      if (s) {
        try {
          s.setItem('crazyCribbage.achievements', JSON.stringify(this.trophies.unlocked));
        } catch (_) {}
      }
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Retrieves player replays, syncing with crazyCribbage.replays.v1 storage.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getReplays(storage = null) {
    const s = getStorage(storage);
    if (!this.replays) {
      this.replays = { byBrand: { crazy: [], clash: [], pints: [], adult: [] }, list: [] };
    }
    if (!this.replays.byBrand) {
      this.replays.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(this.replays.byBrand[b.id])) {
        this.replays.byBrand[b.id] = [];
      }
    }
    if (!Array.isArray(this.replays.list)) {
      this.replays.list = [];
    }

    if (s) {
      try {
        const raw = s.getItem('crazyCribbage.replays.v1');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            let changed = false;
            for (const r of parsed) {
              if (!r || !r.id) continue;
              const brand = r.brand || 'crazy';
              if (!this.replays.list.some(item => item.id === r.id)) {
                this.replays.list.push(r);
                changed = true;
              }
              if (!this.replays.byBrand[brand]) {
                this.replays.byBrand[brand] = [];
              }
              if (!this.replays.byBrand[brand].some(item => item.id === r.id)) {
                this.replays.byBrand[brand].push(r);
                changed = true;
              }
            }
            if (changed) {
              this.replays.list.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
              for (const b of GAME_BRANDS) {
                this.replays.byBrand[b.id].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
              }
            }
          }
        }
      } catch (_) {}
    }

    return this.replays;
  }

  /**
   * Retrieves replays separated for a specific brand or all brands.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  getReplaysByBrand(brand = 'all', storage = null) {
    this.getReplays(storage);
    if (!brand || brand === 'all') {
      return [...this.replays.list].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    }
    const list = this.replays.byBrand[brand] || [];
    return [...list].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  }

  /**
   * Records a match replay into player profile under the appropriate brand.
   * @param {Object} replay
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  recordReplay(replay, storage = null) {
    if (!replay || !replay.id) return this.replays;
    const brand = replay.brand || 'crazy';

    if (!this.replays) {
      this.replays = { byBrand: { crazy: [], clash: [], pints: [], adult: [] }, list: [] };
    }
    if (!this.replays.byBrand) {
      this.replays.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    if (!Array.isArray(this.replays.byBrand[brand])) {
      this.replays.byBrand[brand] = [];
    }
    if (!Array.isArray(this.replays.list)) {
      this.replays.list = [];
    }

    // Remove existing copy if present
    this.replays.list = this.replays.list.filter(r => r.id !== replay.id);
    this.replays.byBrand[brand] = this.replays.byBrand[brand].filter(r => r.id !== replay.id);

    // Unshift newest replay
    this.replays.list.unshift(replay);
    this.replays.byBrand[brand].unshift(replay);

    // Keep within storage capacity limits (cap overall to 30, per-brand to 20)
    this.replays.list = this.replays.list.slice(0, 30);
    this.replays.byBrand[brand] = this.replays.byBrand[brand].slice(0, 20);

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.replays;
  }

  /**
   * Deletes a replay by ID across all brand collections.
   * @param {string} id
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  deleteReplay(id, storage = null) {
    if (!id || !this.replays) return this.replays;
    this.replays.list = (this.replays.list || []).filter(r => r.id !== id);
    for (const b of GAME_BRANDS) {
      if (Array.isArray(this.replays.byBrand[b.id])) {
        this.replays.byBrand[b.id] = this.replays.byBrand[b.id].filter(r => r.id !== id);
      }
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.replays;
  }

  /**
   * Clears saved replays for a specific brand or all brands.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   */
  clearReplays(brand = 'all', storage = null) {
    const s = getStorage(storage);
    if (!this.replays) {
      this.replays = { byBrand: { crazy: [], clash: [], pints: [], adult: [] }, list: [] };
    }

    if (!brand || brand === 'all') {
      this.replays = {
        byBrand: { crazy: [], clash: [], pints: [], adult: [] },
        list: []
      };
      if (s) {
        try {
          s.setItem('crazyCribbage.replays.v1', JSON.stringify([]));
        } catch (_) {}
      }
    } else {
      const removedIds = new Set((this.replays.byBrand[brand] || []).map(r => r.id));
      this.replays.byBrand[brand] = [];
      this.replays.list = this.replays.list.filter(r => !removedIds.has(r.id));
      if (s) {
        try {
          s.setItem('crazyCribbage.replays.v1', JSON.stringify(this.replays.list));
        } catch (_) {}
      }
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Retrieves player's custom 3D boards, categorized by brand and overall list.
   * @param {Object} [storage=null]
   * @returns {{ byBrand: Object, list: Array<Object> }}
   */
  getCustom3DBoards(storage = null) {
    const s = getStorage(storage);
    if (!this.custom3DBoards) {
      this.custom3DBoards = { byBrand: { crazy: [], clash: [], pints: [], adult: [] }, list: [] };
    }
    if (!this.custom3DBoards.byBrand) {
      this.custom3DBoards.byBrand = { crazy: [], clash: [], pints: [], adult: [] };
    }
    for (const b of GAME_BRANDS) {
      if (!Array.isArray(this.custom3DBoards.byBrand[b.id])) {
        this.custom3DBoards.byBrand[b.id] = [];
      }
    }
    if (!Array.isArray(this.custom3DBoards.list)) {
      this.custom3DBoards.list = [];
    }

    if (s) {
      try {
        const raw = s.getItem(STORAGE_KEY_CUSTOM_3D_BOARDS);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            let changed = false;
            for (const b of parsed) {
              if (!b || !b.id) continue;
              const brandVal = b.brand || 'crazy';
              if (!this.custom3DBoards.list.some(item => item.id === b.id)) {
                this.custom3DBoards.list.push(b);
                changed = true;
              }
              const targetBrands = (brandVal === 'all')
                ? GAME_BRANDS.map(g => g.id)
                : brandVal.split(',').map(str => str.trim());
              for (const tb of targetBrands) {
                if (this.custom3DBoards.byBrand[tb] && !this.custom3DBoards.byBrand[tb].some(item => item.id === b.id)) {
                  this.custom3DBoards.byBrand[tb].push(b);
                  changed = true;
                }
              }
            }
          }
        }
      } catch (_) {}
    }

    return this.custom3DBoards;
  }

  /**
   * Retrieves custom 3D boards separated for a specific brand or all brands.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  getCustom3DBoardsByBrand(brand = 'all', storage = null) {
    this.getCustom3DBoards(storage);
    if (!brand || brand === 'all') {
      return [...this.custom3DBoards.list];
    }
    const list = this.custom3DBoards.byBrand[brand] || [];
    return [...list];
  }

  /**
   * Records or updates a custom 3D board into user profile for the appropriate brand.
   * @param {Object} boardConfig
   * @param {string} [brand=null]
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  saveCustom3DBoard(boardConfig, brand = null, storage = null) {
    if (!boardConfig || !boardConfig.id) return null;
    const resolvedBrand = brand || boardConfig.brand || 'crazy';

    this.getCustom3DBoards(storage);

    const clean = {
      ...boardConfig,
      id: String(boardConfig.id).trim(),
      name: String(boardConfig.name || 'Custom Board').trim(),
      brand: resolvedBrand,
      isCustom: true,
      updatedAt: Date.now()
    };

    // Remove existing copy if present
    this.custom3DBoards.list = this.custom3DBoards.list.filter(b => b.id !== clean.id);
    this.custom3DBoards.list.unshift(clean);

    const targetBrands = (resolvedBrand === 'all')
      ? GAME_BRANDS.map(g => g.id)
      : resolvedBrand.split(',').map(s => s.trim());

    for (const b of GAME_BRANDS) {
      if (this.custom3DBoards.byBrand[b.id]) {
        this.custom3DBoards.byBrand[b.id] = this.custom3DBoards.byBrand[b.id].filter(x => x.id !== clean.id);
        if (targetBrands.includes(b.id)) {
          this.custom3DBoards.byBrand[b.id].unshift(clean);
        }
      }
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
    return clean;
  }

  /**
   * Deletes a custom 3D board by ID across all brand collections.
   * @param {string} id
   * @param {Object} [storage=null]
   * @returns {boolean}
   */
  deleteCustom3DBoard(id, storage = null) {
    if (!id || !this.custom3DBoards) return false;
    this.custom3DBoards.list = (this.custom3DBoards.list || []).filter(b => b.id !== id);
    for (const b of GAME_BRANDS) {
      if (Array.isArray(this.custom3DBoards.byBrand[b.id])) {
        this.custom3DBoards.byBrand[b.id] = this.custom3DBoards.byBrand[b.id].filter(x => x.id !== id);
      }
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
    return true;
  }

  /**
   * Clears saved custom 3D boards for a specific brand or all brands.
   * @param {string} [brand='all']
   * @param {Object} [storage=null]
   */
  clearCustom3DBoards(brand = 'all', storage = null) {
    if (!this.custom3DBoards) {
      this.custom3DBoards = { byBrand: { crazy: [], clash: [], pints: [], adult: [] }, list: [] };
    }
    if (!brand || brand === 'all') {
      this.custom3DBoards = {
        byBrand: { crazy: [], clash: [], pints: [], adult: [] },
        list: []
      };
    } else {
      const removedIds = new Set((this.custom3DBoards.byBrand[brand] || []).map(b => b.id));
      this.custom3DBoards.byBrand[brand] = [];
      this.custom3DBoards.list = this.custom3DBoards.list.filter(b => !removedIds.has(b.id));
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Opens the 3D boards gallery & custom loader modal.
   * @param {string} [brand=null]
   */
  static openBoard3DModal(brand = null) {
    if (typeof window !== 'undefined') {
      const targetBrand = brand || 'all';
      const opts = { brand: targetBrand, settings: window.settings };
      if (typeof window.showBoard3DModal === 'function') {
        window.showBoard3DModal(opts);
      } else if (window.crazyCribbageApp && typeof window.crazyCribbageApp.showBoard3DModal === 'function') {
        window.crazyCribbageApp.showBoard3DModal(opts);
      } else if (window.BoardSelectorModal && typeof window.BoardSelectorModal.show === 'function') {
        window.BoardSelectorModal.show(opts);
      } else if (typeof BoardSelectorModal !== 'undefined' && typeof BoardSelectorModal.show === 'function') {
        BoardSelectorModal.show(opts);
      }
    }
  }

  /**
   * Retrieves user's Daily Puzzle streaks and statistics.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getDailyPuzzleStats(storage = null) {
    const s = getStorage(storage);
    if (s) {
      try {
        const raw = s.getItem(STORAGE_KEY_DAILY_PUZZLE);
        if (raw) {
          const parsed = JSON.parse(raw);
          this.stats.dailyPuzzle = {
            ...(this.stats.dailyPuzzle || {}),
            ...parsed
          };
        }
      } catch (_) {}
    }
    const dp = this.stats.dailyPuzzle || {};
    return {
      currentStreak: Number(dp.currentStreak) || 0,
      bestStreak: Number(dp.bestStreak) || 0,
      lastPlayedDate: dp.lastPlayedDate || '',
      history: dp.history || {},
      totalPlayed: Number(dp.totalPlayed) || 0,
      perfectDiscards: Number(dp.perfectDiscards) || 0,
      peggingWins: Number(dp.peggingWins) || 0
    };
  }

  /**
   * Saves updated Daily Puzzle streaks and stats in the user profile.
   * @param {Object} dailyStats
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  setDailyPuzzleStats(dailyStats, storage = null) {
    if (!dailyStats || typeof dailyStats !== 'object') return this.getDailyPuzzleStats(storage);
    this.stats.dailyPuzzle = {
      ...(this.stats.dailyPuzzle || {}),
      ...dailyStats
    };
    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.stats.dailyPuzzle;
  }

  /**
   * Resets Daily Puzzle streak and stats.
   * @param {Object} [storage=null]
   */
  clearDailyPuzzleStats(storage = null) {
    this.stats.dailyPuzzle = {
      currentStreak: 0,
      bestStreak: 0,
      lastPlayedDate: '',
      history: {},
      totalPlayed: 0,
      perfectDiscards: 0,
      peggingWins: 0
    };
    const s = getStorage(storage);
    if (s) {
      try {
        s.setItem(STORAGE_KEY_DAILY_PUZZLE, JSON.stringify(this.stats.dailyPuzzle));
      } catch (_) {}
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Opens the Daily Puzzle modal.
   */
  static openDailyPuzzle() {
    if (typeof window !== 'undefined') {
      if (typeof window.showDailyPuzzleModal === 'function') {
        window.showDailyPuzzleModal();
      } else if (window.modalManager && typeof window.modalManager.showDailyPuzzleModal === 'function') {
        window.modalManager.showDailyPuzzleModal();
      } else if (window.ModalManager && typeof window.ModalManager.showDailyPuzzleModal === 'function') {
        window.ModalManager.showDailyPuzzleModal();
      }
    }
  }

  /**
   * Retrieves player's global Discard Trainer career statistics across all brands.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getDiscardTrainerStats(storage = null) {
    const s = getStorage(storage);
    if (s) {
      try {
        const raw = s.getItem(STORAGE_KEY_DISCARD_TRAINER);
        if (raw) {
          const parsed = JSON.parse(raw);
          this.stats.discardTrainer = {
            ...(this.stats.discardTrainer || {}),
            ...parsed
          };
        }
      } catch (_) {}
    }
    const dt = this.stats.discardTrainer || {};
    const handsPlayed = Number(dt.handsPlayed) || 0;
    const optimalCount = Number(dt.optimalCount) || 0;
    const currentStreak = Number(dt.currentStreak) || 0;
    const bestStreak = Number(dt.bestStreak) || 0;
    const totalLossEV = Number(dt.totalLossEV) || 0;
    const accuracyPct = handsPlayed > 0 ? Math.round((optimalCount / handsPlayed) * 100) : 0;
    const averageLossEV = handsPlayed > 0 ? Math.round((totalLossEV / handsPlayed) * 100) / 100 : 0;

    return {
      handsPlayed,
      optimalCount,
      currentStreak,
      bestStreak,
      totalLossEV: Math.round(totalLossEV * 100) / 100,
      averageLossEV,
      accuracyPct,
      lastPlayedDate: dt.lastPlayedDate || ''
    };
  }

  /**
   * Records a Discard Trainer practice hand into global user profile.
   * Tracks career hands played, optimal discards, accuracy %, win/optimal streaks, and EV loss.
   * @param {Object} playData
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  recordDiscardTrainerPlay(playData = {}, storage = null) {
    if (!this.stats.discardTrainer) {
      this.stats.discardTrainer = {
        handsPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        accuracyPct: 0,
        lastPlayedDate: ''
      };
    }
    const dt = this.stats.discardTrainer;
    const isOptimal = Boolean(playData.isOptimal);
    const diff = Math.max(0, Number(playData.diff ?? playData.evDiff) || 0);
    const dateStr = playData.date || new Date().toISOString().slice(0, 10);

    dt.handsPlayed = (Number(dt.handsPlayed) || 0) + 1;
    if (isOptimal) {
      dt.optimalCount = (Number(dt.optimalCount) || 0) + 1;
      dt.currentStreak = (Number(dt.currentStreak) || 0) + 1;
      dt.bestStreak = Math.max(Number(dt.bestStreak) || 0, dt.currentStreak);
    } else {
      dt.currentStreak = 0;
    }
    dt.totalLossEV = (Number(dt.totalLossEV) || 0) + diff;
    dt.accuracyPct = Math.round((dt.optimalCount / dt.handsPlayed) * 100);
    dt.averageLossEV = Math.round((dt.totalLossEV / dt.handsPlayed) * 100) / 100;
    dt.lastPlayedDate = dateStr;

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.getDiscardTrainerStats(storage);
  }

  /**
   * Sets or updates Discard Trainer stats directly in user profile.
   * @param {Object} stats
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  setDiscardTrainerStats(stats, storage = null) {
    if (!stats || typeof stats !== 'object') return this.getDiscardTrainerStats(storage);
    this.stats.discardTrainer = {
      ...(this.stats.discardTrainer || {}),
      ...stats
    };
    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.getDiscardTrainerStats(storage);
  }

  /**
   * Resets Discard Trainer statistics to zero across the profile.
   * @param {Object} [storage=null]
   */
  clearDiscardTrainerStats(storage = null) {
    this.stats.discardTrainer = {
      handsPlayed: 0,
      optimalCount: 0,
      currentStreak: 0,
      bestStreak: 0,
      totalLossEV: 0,
      averageLossEV: 0,
      accuracyPct: 0,
      lastPlayedDate: ''
    };
    const s = getStorage(storage);
    if (s) {
      try {
        s.setItem(STORAGE_KEY_DISCARD_TRAINER, JSON.stringify(this.stats.discardTrainer));
      } catch (_) {}
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Opens the Discard Trainer modal.
   * @param {Object} [context={}]
   */
  static openDiscardTrainer(context = {}) {
    if (typeof window !== 'undefined') {
      if (typeof window.showDiscardTrainerModal === 'function') {
        window.showDiscardTrainerModal(context);
      } else if (window.modalManager && typeof window.modalManager.showDiscardTrainerModal === 'function') {
        window.modalManager.showDiscardTrainerModal(context);
      } else if (window.ModalManager && typeof window.ModalManager.showDiscardTrainerModal === 'function') {
        window.ModalManager.showDiscardTrainerModal(context);
      }
    }
  }

  /**
   * Retrieves user Pegging Tactics Trainer career stats.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getPeggingTrainerStats(storage = null) {
    const s = getStorage(storage);
    if (s && (!this.stats.peggingTrainer || !this.stats.peggingTrainer.scenariosPlayed)) {
      try {
        const raw = s.getItem(STORAGE_KEY_PEGGING_TRAINER);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            this.stats.peggingTrainer = { ...(this.stats.peggingTrainer || {}), ...parsed };
          }
        }
      } catch (_) {}
    }
    const pt = this.stats.peggingTrainer || {};
    const scenariosPlayed = Number(pt.scenariosPlayed) || 0;
    const optimalCount = Number(pt.optimalCount) || 0;
    const currentStreak = Number(pt.currentStreak) || 0;
    const bestStreak = Number(pt.bestStreak) || 0;
    const totalLossEV = Number(pt.totalLossEV) || 0;
    const accuracyPct = scenariosPlayed > 0 ? Math.round((optimalCount / scenariosPlayed) * 100) : 0;
    const averageLossEV = scenariosPlayed > 0 ? Math.round((totalLossEV / scenariosPlayed) * 100) / 100 : 0;

    return {
      scenariosPlayed,
      optimalCount,
      currentStreak,
      bestStreak,
      totalLossEV: Math.round(totalLossEV * 100) / 100,
      averageLossEV,
      accuracyPct,
      lastPlayedDate: pt.lastPlayedDate || ''
    };
  }

  /**
   * Records a Pegging Trainer scenario play into global user profile.
   * @param {Object} playData
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  recordPeggingTrainerPlay(playData = {}, storage = null) {
    if (!this.stats.peggingTrainer) {
      this.stats.peggingTrainer = {
        scenariosPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        accuracyPct: 0,
        lastPlayedDate: ''
      };
    }
    const pt = this.stats.peggingTrainer;
    const isOptimal = Boolean(playData.isOptimal);
    const diff = Math.max(0, Number(playData.diff ?? playData.evDiff) || 0);
    const dateStr = playData.date || new Date().toISOString().slice(0, 10);

    pt.scenariosPlayed = (Number(pt.scenariosPlayed) || 0) + 1;
    if (isOptimal) {
      pt.optimalCount = (Number(pt.optimalCount) || 0) + 1;
      pt.currentStreak = (Number(pt.currentStreak) || 0) + 1;
      pt.bestStreak = Math.max(Number(pt.bestStreak) || 0, pt.currentStreak);
    } else {
      pt.currentStreak = 0;
    }
    pt.totalLossEV = (Number(pt.totalLossEV) || 0) + diff;
    pt.accuracyPct = Math.round((pt.optimalCount / pt.scenariosPlayed) * 100);
    pt.averageLossEV = Math.round((pt.totalLossEV / pt.scenariosPlayed) * 100) / 100;
    pt.lastPlayedDate = dateStr;

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.getPeggingTrainerStats(storage);
  }

  /**
   * Sets or updates Pegging Trainer stats directly in user profile.
   * @param {Object} stats
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  setPeggingTrainerStats(stats, storage = null) {
    if (!stats || typeof stats !== 'object') return this.getPeggingTrainerStats(storage);
    this.stats.peggingTrainer = {
      ...(this.stats.peggingTrainer || {}),
      ...stats
    };
    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.getPeggingTrainerStats(storage);
  }

  /**
   * Resets Pegging Trainer statistics to zero across the profile.
   * @param {Object} [storage=null]
   */
  clearPeggingTrainerStats(storage = null) {
    this.stats.peggingTrainer = {
      scenariosPlayed: 0,
      optimalCount: 0,
      currentStreak: 0,
      bestStreak: 0,
      totalLossEV: 0,
      averageLossEV: 0,
      accuracyPct: 0,
      lastPlayedDate: ''
    };
    const s = getStorage(storage);
    if (s) {
      try {
        s.setItem(STORAGE_KEY_PEGGING_TRAINER, JSON.stringify(this.stats.peggingTrainer));
      } catch (_) {}
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Retrieves player's Counting Trainer statistics.
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  getCountingTrainerStats(storage = null) {
    const s = getStorage(storage);
    if (s) {
      try {
        const raw = s.getItem(STORAGE_KEY_COUNTING_TRAINER);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            this.stats.countingTrainer = { ...(this.stats.countingTrainer || {}), ...parsed };
          }
        }
      } catch (_) {}
    }
    const ct = this.stats.countingTrainer || {};
    const handsPlayed = Number(ct.handsPlayed) || 0;
    const handsCorrect = Number(ct.handsCorrect) || 0;
    const handsWrong = Number(ct.handsWrong) || 0;
    const currentStreak = Number(ct.currentStreak) || 0;
    const bestStreak = Number(ct.bestStreak) || 0;
    const accuracyPct = handsPlayed > 0 ? Math.round((handsCorrect / handsPlayed) * 100) : 0;

    return {
      handsPlayed,
      handsCorrect,
      handsWrong,
      currentStreak,
      bestStreak,
      accuracyPct,
      lastPlayedDate: ct.lastPlayedDate || ''
    };
  }

  /**
   * Records a Counting Trainer play into global user profile.
   * @param {Object} playData
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  recordCountingTrainerPlay(playData = {}, storage = null) {
    if (!this.stats.countingTrainer) {
      this.stats.countingTrainer = {
        handsPlayed: 0,
        handsCorrect: 0,
        handsWrong: 0,
        currentStreak: 0,
        bestStreak: 0,
        accuracyPct: 0,
        lastPlayedDate: ''
      };
    }
    const ct = this.stats.countingTrainer;
    const isCorrect = Boolean(playData.isCorrect);
    const dateStr = playData.date || new Date().toISOString().slice(0, 10);

    ct.handsPlayed = (Number(ct.handsPlayed) || 0) + 1;
    if (isCorrect) {
      ct.handsCorrect = (Number(ct.handsCorrect) || 0) + 1;
      ct.currentStreak = (Number(ct.currentStreak) || 0) + 1;
      ct.bestStreak = Math.max(Number(ct.bestStreak) || 0, ct.currentStreak);
    } else {
      ct.handsWrong = (Number(ct.handsWrong) || 0) + 1;
      ct.currentStreak = 0;
    }
    ct.accuracyPct = Math.round((ct.handsCorrect / ct.handsPlayed) * 100);
    ct.lastPlayedDate = dateStr;

    const s = getStorage(storage);
    if (s) {
      try {
        s.setItem(STORAGE_KEY_COUNTING_TRAINER, JSON.stringify(ct));
      } catch (_) {}
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.getCountingTrainerStats(storage);
  }

  /**
   * Resets Counting Trainer statistics.
   * @param {Object} [storage=null]
   */
  clearCountingTrainerStats(storage = null) {
    this.stats.countingTrainer = {
      handsPlayed: 0,
      handsCorrect: 0,
      handsWrong: 0,
      currentStreak: 0,
      bestStreak: 0,
      accuracyPct: 0,
      lastPlayedDate: ''
    };
    const s = getStorage(storage);
    if (s) {
      try { s.removeItem(STORAGE_KEY_COUNTING_TRAINER); } catch (_) {}
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Opens the Counting Trainer modal.
   * @param {Object} [context={}]
   */
  static openCountingTrainer(context = {}) {
    if (typeof window !== 'undefined') {
      if (typeof window.showCountingTrainerModal === 'function') {
        window.showCountingTrainerModal(context);
      } else if (window.modalManager && typeof window.modalManager.showCountingTrainerModal === 'function') {
        window.modalManager.showCountingTrainerModal(context);
      }
    }
  }

  /**
   * Opens the Pegging Tactics Trainer modal.
   * @param {Object} [context={}]
   */
  static openPeggingTrainer(context = {}) {
    if (typeof window !== 'undefined') {
      if (typeof window.showPeggingTrainerModal === 'function') {
        window.showPeggingTrainerModal(context);
      } else if (window.modalManager && typeof window.modalManager.showPeggingTrainerModal === 'function') {
        window.modalManager.showPeggingTrainerModal(context);
      } else if (window.ModalManager && typeof window.ModalManager.showPeggingTrainerModal === 'function') {
        window.ModalManager.showPeggingTrainerModal(context);
      }
    }
  }

  /**
   * Returns match history list, optionally filtered by brand.
   * @param {number} [limit=50]
   * @param {string} [brand=null]
   * @returns {Array<Object>}
   */
  getHistory(limit = 50, brand = null) {
    let list = this.history || [];
    if (brand && brand !== 'all') {
      list = list.filter(m => (m.brand || 'crazy') === brand);
    }
    return list.slice(0, limit);
  }

  /**
   * Records a completed match into player records.
   * @param {Object} gameData
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  recordGame(gameData = {}, storage = null) {
    const winner = gameData.winner;
    const userScore = Number(gameData.userScore) || 0;
    const oppScore = Number(gameData.oppScore) || 0;
    const userWon = Boolean(
      winner === 'you' ||
      winner === 'p1' ||
      winner === 'user' ||
      winner === 'team1' ||
      winner === true ||
      (gameData.userWon !== undefined ? gameData.userWon : (userScore > oppScore && oppScore > 0))
    );
    const skunk = Boolean(gameData.skunk || gameData.isSkunk);
    const brand = gameData.brand || (typeof window !== 'undefined' && window.getAppBrand ? window.getAppBrand() : 'crazy');

    // Update totals
    this.stats.totalGamesPlayed = (this.stats.totalGamesPlayed || 0) + 1;
    if (userWon) {
      this.stats.wins = (this.stats.wins || 0) + 1;
      const curStreak = this.stats.currentStreak || 0;
      this.stats.currentStreak = curStreak > 0 ? curStreak + 1 : 1;
      this.stats.bestStreak = Math.max(this.stats.bestStreak || 0, this.stats.currentStreak);
      if (skunk) {
        this.stats.skunkWins = (this.stats.skunkWins || 0) + 1;
      }
    } else {
      this.stats.losses = (this.stats.losses || 0) + 1;
      const curStreak = this.stats.currentStreak || 0;
      this.stats.currentStreak = curStreak < 0 ? curStreak - 1 : -1;
      if (skunk) {
        this.stats.skunkLosses = (this.stats.skunkLosses || 0) + 1;
      }
    }

    if (userScore > 0) {
      this.stats.highestScore = Math.max(this.stats.highestScore || 0, userScore);
      this.stats.totalPoints = (this.stats.totalPoints || 0) + userScore;
    }
    this.stats.lastPlayed = Date.now();

    // Update per-brand stats
    if (!this.stats.byBrand) {
      this.stats.byBrand = {};
    }
    if (!this.stats.byBrand[brand]) {
      this.stats.byBrand[brand] = { totalGamesPlayed: 0, wins: 0, losses: 0, highestScore: 0, skunkWins: 0, skunkLosses: 0 };
    }
    const bStats = this.stats.byBrand[brand];
    bStats.totalGamesPlayed = (bStats.totalGamesPlayed || 0) + 1;
    if (userWon) {
      bStats.wins = (bStats.wins || 0) + 1;
      if (skunk) bStats.skunkWins = (bStats.skunkWins || 0) + 1;
    } else {
      bStats.losses = (bStats.losses || 0) + 1;
      if (skunk) bStats.skunkLosses = (bStats.skunkLosses || 0) + 1;
    }
    if (userScore > 0) {
      bStats.highestScore = Math.max(bStats.highestScore || 0, userScore);
    }

    // Create match history entry
    const matchEntry = {
      id: 'match_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      timestamp: Date.now(),
      date: new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }),
      time: new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }),
      winner: userWon ? 'you' : 'opp',
      userWon,
      userScore,
      oppScore,
      opponentName: gameData.opponentName || 'Opponent',
      variantKey: gameData.variantKey || 'standard',
      variantLabel: gameData.variantLabel || 'Standard Match',
      brand: brand,
      skunk
    };

    this.history.unshift(matchEntry);
    if (this.history.length > 100) {
      this.history = this.history.slice(0, 100);
    }

    this.save(storage);
    Profile.notifyProfileUpdate();
    return this.getStats();
  }

  /**
   * Resets game match records and statistics.
   * Can reset all 4 games or a specific game brand.
   * @param {Object} [storage=null]
   * @param {string} [brand=null]
   */
  clearRecords(storage = null, brand = null) {
    const s = getStorage(storage);
    const settingsMgr = (typeof window !== 'undefined' && window.CrazyCribbage?.SettingsManager) || null;

    if (brand === 'daily' || brand === 'dailyPuzzle') {
      this.clearDailyPuzzleStats(storage);
      return;
    }

    if (brand === 'trainer' || brand === 'discardTrainer') {
      this.clearDiscardTrainerStats(storage);
      return;
    }

    if (brand === 'pegging' || brand === 'peggingTrainer') {
      this.clearPeggingTrainerStats(storage);
      return;
    }

    if (brand === 'boards' || brand === 'custom3DBoards' || brand === 'customBoards') {
      this.clearCustom3DBoards('all', storage);
      return;
    }

    if (brand && brand !== 'all') {
      if (settingsMgr && typeof settingsMgr.saveRecords === 'function') {
        settingsMgr.saveRecords({}, s, brand);
      }
      this.history = (this.history || []).filter(m => (m.brand || 'crazy') !== brand);
      if (this.stats?.byBrand?.[brand]) {
        delete this.stats.byBrand[brand];
      }
      const overall = this.getOverallStats(storage);
      this.stats.wins = overall.wins;
      this.stats.losses = overall.losses;
      this.stats.totalGamesPlayed = overall.totalGamesPlayed;
    } else {
      if (settingsMgr && typeof settingsMgr.saveRecords === 'function') {
        for (const b of GAME_BRANDS) {
          settingsMgr.saveRecords({}, s, b.id);
        }
      }
      const preservedDaily = this.stats.dailyPuzzle || {
        currentStreak: 0,
        bestStreak: 0,
        lastPlayedDate: '',
        history: {},
        totalPlayed: 0,
        perfectDiscards: 0,
        peggingWins: 0
      };
      const preservedTrainer = this.stats.discardTrainer || {
        handsPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        accuracyPct: 0,
        lastPlayedDate: ''
      };
      const preservedPeggingTrainer = this.stats.peggingTrainer || {
        scenariosPlayed: 0,
        optimalCount: 0,
        currentStreak: 0,
        bestStreak: 0,
        totalLossEV: 0,
        averageLossEV: 0,
        accuracyPct: 0,
        lastPlayedDate: ''
      };
      this.stats = {
        totalGamesPlayed: 0,
        wins: 0,
        losses: 0,
        currentStreak: 0,
        bestStreak: 0,
        skunkWins: 0,
        skunkLosses: 0,
        highestScore: 0,
        totalPoints: 0,
        lastPlayed: null,
        byBrand: {},
        dailyPuzzle: preservedDaily,
        discardTrainer: preservedTrainer,
        peggingTrainer: preservedPeggingTrainer
      };
      this.history = [];
    }
    this.save(storage);
    Profile.notifyProfileUpdate();
  }

  /**
   * Dispatches 'profile-updated' event to keep UI and menu dropdowns in sync.
   */
  static notifyProfileUpdate() {
    if (typeof window !== 'undefined') {
      if (typeof window.dispatchEvent === 'function' && typeof CustomEvent !== 'undefined') {
        try {
          window.dispatchEvent(new CustomEvent('profile-updated'));
        } catch (_) {}
      }
      if (typeof window.updateProfileDropdownUI === 'function') {
        window.updateProfileDropdownUI();
      }
    }
  }

  /**
   * Switches active filter tab in records modal.
   * @param {string} tabId
   */
  static switchRecordsTab(tabId) {
    Profile._selectedRecordsTab = tabId;
    Profile.showRecordsModal(Profile._lastContext || {});
  }

  /**
   * Displays the enhanced Game Records Modal featuring Player Profile & 4 games win records.
   * @param {Object} [context={}]
   */
  static showRecordsModal(context = {}) {
    Profile._lastContext = context;
    const profile = Profile.getInstance(context.storage);
    const playerName = profile.getName(context.storage);
    const avId = profile.getAvatar(context.storage);
    const avMgr = (typeof window !== 'undefined' && window.AvatarManager) || AvatarManager;
    const avBadgeHtml = avMgr && typeof avMgr.renderAvatarBadge === 'function'
      ? avMgr.renderAvatarBadge(avId, { size: 52, showRing: true })
      : '<span style="font-size:2rem;">👤</span>';
    const avObj = avMgr ? avMgr.getAvatarById(avId, getStorage(context.storage)) : null;

    const gameRecs = profile.getGameRecords(context.storage);
    const overallStats = profile.getOverallStats(context.storage);
    const activeTab = Profile._selectedRecordsTab || 'all';

    // Career streaks display
    let streakText = 'None';
    if (overallStats.currentStreak > 0) {
      streakText = `🔥 ${overallStats.currentStreak} Win Streak`;
    } else if (overallStats.currentStreak < 0) {
      streakText = `❄️ ${Math.abs(overallStats.currentStreak)} Loss Streak`;
    }

    // Match history cards / rows
    const history = profile.getHistory(25, activeTab);
    const historyRowsHtml = history.length
      ? history.map(m => {
        const resultLabel = m.userWon ? 'WIN' : 'LOSS';
        const badgeColor = m.userWon ? '#10b981' : '#ef4444';
        const skunkBadge = m.skunk ? `<span style="background:#f43f5e;color:#fff;font-size:0.68rem;padding:2px 6px;border-radius:6px;font-weight:700;margin-left:6px;">SKUNK</span>` : '';
        const brandObj = GAME_BRANDS.find(b => b.id === (m.brand || 'crazy')) || GAME_BRANDS[0];
        return `
          <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 10px;border-bottom:1px solid rgba(244,234,212,0.1);gap:10px;">
            <div style="flex:1;min-width:0;">
              <div style="font-weight:600;color:#f3f4f6;font-size:0.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                vs. ${m.opponentName} ${skunkBadge}
              </div>
              <div style="font-size:0.75rem;color:#9ca3af;display:flex;align-items:center;gap:6px;margin-top:2px;">
                <span>${m.date}</span> · 
                <span style="color:${brandObj.color};font-weight:700;">${brandObj.icon} ${brandObj.shortName}</span> · 
                <span>${m.variantLabel}</span>
              </div>
            </div>
            <div style="text-align:right;white-space:nowrap;">
              <div style="font-weight:700;color:#facc15;font-size:0.92rem;">
                ${m.userScore} – ${m.oppScore}
              </div>
              <span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:0.7rem;font-weight:800;background:${badgeColor};color:#fff;">
                ${resultLabel}
              </span>
            </div>
          </div>
        `;
      }).join('')
      : '<div style="color:#d7e8dc;font-style:italic;padding:12px 0;text-align:center;">No match history recorded yet.</div>';

    // Build the 4 games breakdown sections
    const displayBrands = activeTab === 'all'
      ? GAME_BRANDS
      : GAME_BRANDS.filter(b => b.id === activeTab);

    const gamesBreakdownHtml = displayBrands.map(b => {
      const rec = gameRecs[b.id];
      return `
        <div class="game-brand-records-card" style="background:rgba(0,0,0,0.3);border:1.5px solid ${b.border};border-radius:10px;padding:10px 14px;margin-bottom:10px;text-align:left;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;border-bottom:1px solid rgba(244,234,212,0.1);padding-bottom:6px;flex-wrap:wrap;gap:8px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="font-size:1.3rem;">${b.icon}</span>
              <div>
                <div style="color:${b.color};font-size:0.98rem;font-weight:800;">${b.name}</div>
                <div style="font-size:0.72rem;color:#9ca3af;">Win Records &amp; Setups</div>
              </div>
            </div>
            <div style="text-align:right;white-space:nowrap;">
              <div style="font-size:1.05rem;font-weight:800;color:#facc15;">${rec.wins} Wins – ${rec.losses} Losses</div>
              <span style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:0.72rem;font-weight:800;background:${rec.wins > 0 ? '#10b981' : '#4b5563'};color:#fff;">${rec.winRate} Win Rate</span>
            </div>
          </div>
          ${rec.variants.length ? `
            <div style="display:grid;grid-template-columns:repeat(auto-fit, minmax(200px, 1fr));gap:6px;margin-top:6px;">
              ${rec.variants.map(v => `
                <div style="display:flex;justify-content:space-between;padding:4px 8px;background:rgba(255,255,255,0.03);border-radius:6px;font-size:0.83rem;border:1px solid rgba(244,234,212,0.06);">
                  <span style="color:#e5e7eb;">${v.label}</span>
                  <strong style="color:#facc15;">${v.wins}–${v.losses}</strong>
                </div>
              `).join('')}
            </div>
          ` : `<div style="font-size:0.8rem;color:#9ca3af;font-style:italic;padding:4px 0;">No match setups recorded yet in ${b.name}.</div>`}
          <div style="display:flex;align-items:center;justify-content:space-between;margin-top:8px;padding-top:6px;border-top:1px solid rgba(244,234,212,0.08);flex-wrap:wrap;gap:6px;">
            <div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">
              <span style="font-size:0.76rem;color:#facc15;font-weight:700;">
                🏆 ${rec.trophiesUnlocked} of ${rec.trophiesTotal} Trophies (${rec.trophiesPct}%)
              </span>
              <span style="font-size:0.76rem;color:${b.color};font-weight:700;">
                🎬 ${rec.replaysCount} Saved Replays
              </span>
              <span style="font-size:0.76rem;color:#38bdf8;font-weight:700;">
                🎲 ${rec.customBoardsCount} Custom 3D Boards
              </span>
            </div>
            <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
              <button type="button" data-action="view-trophies" data-brand="${b.id}" style="font-size:0.72rem;padding:3px 9px;border-radius:6px;border:1px solid ${b.color};background:${b.badgeBg};color:${b.color};cursor:pointer;font-weight:700;">
                🏆 View ${b.shortName} Trophies
              </button>
              <button type="button" data-action="view-replays" data-brand="${b.id}" style="font-size:0.72rem;padding:3px 9px;border-radius:6px;border:1px solid ${b.color};background:${b.badgeBg};color:${b.color};cursor:pointer;font-weight:700;">
                🎬 View ${b.shortName} Replays
              </button>
              <button type="button" data-action="open-3d-boards" data-brand="${b.id}" style="font-size:0.72rem;padding:3px 9px;border-radius:6px;border:1px solid #38bdf8;background:rgba(56, 189, 248, 0.2);color:#38bdf8;cursor:pointer;font-weight:700;">
                🎲 ${b.shortName} 3D Boards
              </button>
            </div>
          </div>
        </div>
      `;
    }).join('');

    const modalHtml = `
      <div class="profile-records-modal-content">
        <!-- Player Header Banner -->
        <div style="background:linear-gradient(135deg, rgba(30, 27, 75, 0.9), rgba(15, 23, 42, 0.95));border:1.5px solid rgba(245,158,11,0.5);border-radius:12px;padding:14px;margin-bottom:14px;display:flex;align-items:center;gap:14px;">
          <div class="profile-header-avatar-wrap" style="position:relative;flex-shrink:0;">
            <button type="button" class="profile-avatar-trigger" id="btnProfileModalAvatar" data-action="open-avatar" title="Click to choose a different avatar persona" aria-label="Change Avatar">
              ${avBadgeHtml}
              <span class="profile-avatar-edit-chip" title="Change Avatar">🎭</span>
            </button>
          </div>
          <div style="flex:1;min-width:0;text-align:left;">
            <!-- Username row (view mode) -->
            <div id="profileHeaderNameRow" style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <span id="profileHeaderNameDisplay" style="font-size:1.25rem;font-weight:800;color:#fff;">${playerName}</span>
              <button type="button" class="btn-profile-edit-name" id="btnEditProfileName" data-action="edit-name" title="Edit Username" aria-label="Edit Username">
                ✏️ Edit
              </button>
              <span id="profilePersonaTag" style="font-size:0.75rem;padding:2px 8px;border-radius:10px;background:rgba(245,158,11,0.2);color:#f59e0b;border:1px solid rgba(245,158,11,0.4);font-weight:700;">
                ${avObj?.name || 'Persona'}
              </span>
            </div>

            <!-- Username row (edit mode) -->
            <div id="profileHeaderNameEditRow" style="display:none;align-items:center;gap:6px;flex-wrap:wrap;margin:4px 0;">
              <input type="text" id="inputProfileInlineName" value="${playerName.replace(/"/g, '&quot;')}" maxlength="24" placeholder="Enter username" pattern="[a-zA-Z0-9]+" style="padding:4px 8px;border-radius:6px;border:1.5px solid #f59e0b;background:#0f172a;color:#fff;font-weight:700;font-size:0.95rem;outline:none;width:150px;max-width:100%;" />
              <button type="button" class="btn-gold" id="btnSaveInlineName" data-action="save-inline-name" style="padding:4px 10px;font-size:0.8rem;border-radius:6px;cursor:pointer;font-weight:700;">Save</button>
              <button type="button" class="btn-ghost" id="btnCancelInlineName" data-action="cancel-inline-name" style="padding:4px 8px;font-size:0.8rem;border-radius:6px;cursor:pointer;color:#9ca3af;">Cancel</button>
            </div>

            <div style="font-size:0.88rem;color:#facc15;font-weight:700;margin-top:4px;">
              ${overallStats.wins} Wins · ${overallStats.losses} Losses <span style="opacity:0.75;color:#e5e7eb;">(${overallStats.winRate} Win Rate)</span>
            </div>
            <div style="font-size:0.78rem;color:#9ca3af;margin-top:2px;">
              ${streakText} · Best Win Streak: ${overallStats.bestStreak}
            </div>
            <div style="font-size:0.76rem;color:#facc15;margin-top:3px;font-weight:700;display:flex;align-items:center;gap:8px;flex-wrap:wrap;">
              <span>🏆 ${overallStats.trophiesUnlocked} of ${overallStats.trophiesTotal} Badges Unlocked (${overallStats.trophiesPct}%)</span>
              <button type="button" data-action="view-trophies" data-brand="all" style="font-size:0.7rem;padding:2px 8px;border-radius:5px;border:1px solid #facc15;background:rgba(250,204,21,0.2);color:#facc15;cursor:pointer;font-weight:700;">
                Trophy Room
              </button>
              <span>·</span>
              <span style="color:#cbd5e1;">🎬 ${overallStats.replaysTotal} Replays</span>
              <button type="button" data-action="view-replays" data-brand="all" style="font-size:0.7rem;padding:2px 8px;border-radius:5px;border:1px solid #38bdf8;background:rgba(56,189,248,0.2);color:#38bdf8;cursor:pointer;font-weight:700;">
                Replay Library
              </button>
              <span>·</span>
              <span style="color:#38bdf8;">🎲 ${overallStats.customBoardsTotal} Custom 3D Boards</span>
              <button type="button" data-action="open-3d-boards" data-brand="all" style="font-size:0.7rem;padding:2px 8px;border-radius:5px;border:1px solid #38bdf8;background:rgba(56,189,248,0.2);color:#38bdf8;cursor:pointer;font-weight:700;">
                3D Boards
              </button>
              <span>·</span>
              <span style="color:#34d399;">🎯 ${overallStats.discardTrainer.optimalCount}/${overallStats.discardTrainer.handsPlayed} Optimal (${overallStats.discardTrainer.accuracyPct}%)</span>
              <button type="button" data-action="open-trainer" style="font-size:0.7rem;padding:2px 8px;border-radius:5px;border:1px solid #10b981;background:rgba(16,185,129,0.2);color:#34d399;cursor:pointer;font-weight:700;">
                Discard Trainer
              </button>
            </div>
            <div style="margin-top:4px; display: none;">
              <span style="font-size:0.7rem;padding:2px 8px;border-radius:10px;background:rgba(56,189,248,0.15);color:#38bdf8;border:1px solid rgba(56,189,248,0.35);font-weight:600;">
                🌐 Global Profile across all 4 games
              </span>
            </div>
          </div>
        </div>

        <!-- 4 Games Win Records Hub Cards (Separated by Game) -->
        <div class="games-record-grid" style="display:grid;grid-template-columns:repeat(4, 1fr);gap:8px;margin-bottom:12px;text-align:center;">
          ${GAME_BRANDS.map(b => {
            const rec = gameRecs[b.id];
            const isSelected = activeTab === b.id;
            return `
              <div class="game-record-summary-card ${isSelected ? 'active' : ''}" data-action="switch-tab" data-tab="${b.id}" title="Click to filter ${b.name}" style="background:${isSelected ? b.badgeBg : 'rgba(0,0,0,0.3)'};border:1.5px solid ${isSelected ? b.color : 'rgba(244,234,212,0.15)'};border-radius:10px;padding:8px 4px;cursor:pointer;transition:transform 0.15s ease, border-color 0.15s ease;">
                <div style="font-size:1.15rem;margin-bottom:2px;">${b.icon}</div>
                <div style="font-size:0.75rem;font-weight:700;color:${b.color};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${b.shortName}</div>
                <div style="font-size:1.1rem;font-weight:800;color:#fff;margin:2px 0;">${rec.wins}–${rec.losses}</div>
                <div style="font-size:0.68rem;font-weight:700;color:${rec.wins > 0 ? '#10b981' : '#9ca3af'};">${rec.winRate}</div>
                <div style="font-size:0.67rem;font-weight:700;color:#facc15;margin-top:3px;background:rgba(250,204,21,0.12);padding:2px 4px;border-radius:4px;border:1px solid rgba(250,204,21,0.25);display:flex;justify-content:center;gap:6px;flex-wrap:wrap;">
                  <span>🏆 ${rec.trophiesUnlocked}/${rec.trophiesTotal}</span>
                  <span style="color:${b.color};">🎬 ${rec.replaysCount}</span>
                  <span style="color:#38bdf8;">🎲 ${rec.customBoardsCount}</span>
                </div>
              </div>
            `;
          }).join('')}
        </div>

        <!-- Game & Mode Filter Dropdown (replaces overflowing horizontal scrollbar) -->
        <div class="profile-tabs-bar" id="profileFilterDropdownRow" style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:12px;background:rgba(0,0,0,0.28);border:1.5px solid rgba(245,158,11,0.35);border-radius:10px;padding:8px 12px;flex-wrap:wrap;">
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-size:1.15rem;">🎯</span>
            <label for="selectProfileRecordsTab" style="font-size:0.84rem;font-weight:800;color:#facc15;text-transform:uppercase;letter-spacing:0.04em;">
              Filter Records &amp; Mode:
            </label>
          </div>
          <div style="flex:1;min-width:240px;position:relative;">
            <select id="selectProfileRecordsTab" class="profile-records-tab-select" aria-label="Filter profile records and stats by game or mode" style="width:100%;padding:8px 32px 8px 12px;border-radius:8px;border:1.5px solid #f59e0b;background:#0f172a;color:#facc15;font-weight:700;font-size:0.9rem;cursor:pointer;outline:none;appearance:none;-webkit-appearance:none;box-shadow:0 2px 8px rgba(0,0,0,0.4);">
              <option value="all" ${activeTab === 'all' ? 'selected' : ''}>🌐 All 4 Games (${overallStats.wins} Wins – ${overallStats.losses} Losses)</option>
              ${GAME_BRANDS.map(b => {
                const rec = gameRecs[b.id];
                return `<option value="${b.id}" ${activeTab === b.id ? 'selected' : ''}>${b.icon} ${b.name} (${rec.wins}W – ${rec.losses}L)</option>`;
              }).join('')}
              <option value="boards" ${activeTab === 'boards' ? 'selected' : ''}>🎲 3D Boards (${overallStats.customBoardsTotal})</option>
              <option value="daily" ${activeTab === 'daily' ? 'selected' : ''}>🧩 Daily Puzzle (${profile.getDailyPuzzleStats(context.storage).currentStreak > 0 ? `${profile.getDailyPuzzleStats(context.storage).currentStreak}🔥 Day Streak` : `${profile.getDailyPuzzleStats(context.storage).totalPlayed} Played`})</option>
              <option value="trainer" ${activeTab === 'trainer' ? 'selected' : ''}>🎯 Discard Trainer (${overallStats.discardTrainer.handsPlayed > 0 ? `${overallStats.discardTrainer.accuracyPct}% Optimal` : '0 Practiced'})</option>
            </select>
            <span style="position:absolute;right:12px;top:50%;transform:translateY(-50%);pointer-events:none;color:#f59e0b;font-size:0.75rem;">▼</span>
          </div>
        </div>

        ${(activeTab !== 'daily' && activeTab !== 'trainer' && activeTab !== 'boards') ? `
          <!-- Game Records Breakdown Separated by 4 Games -->
          <div style="text-align:left;margin-bottom:12px;">
            <div style="font-size:0.8rem;text-transform:uppercase;letter-spacing:0.06em;color:#f59e0b;font-weight:700;margin-bottom:8px;">
              ${activeTab === 'all' ? 'Records Separated by Game' : `${(GAME_BRANDS.find(b => b.id === activeTab)?.name) || 'Game'} Setups`}
            </div>
            <div style="max-height:36vh;overflow-y:auto;padding-right:2px;">
              ${gamesBreakdownHtml}
            </div>
          </div>
        ` : ''}

        ${(activeTab === 'all' || activeTab === 'daily') ? (() => {
          const dp = profile.getDailyPuzzleStats(context.storage);
          const todayKey = (typeof window !== 'undefined' && window.DailyPuzzleManager && typeof window.DailyPuzzleManager.getDailyDateKey === 'function')
            ? window.DailyPuzzleManager.getDailyDateKey()
            : new Date().toISOString().slice(0, 10);
          const isTodayDone = Boolean(dp.lastPlayedDate === todayKey && dp.history?.[todayKey]?.completed);
          return `
            <!-- Daily Puzzle Streaks & Statistics -->
            <div class="profile-daily-puzzle-card" style="background:rgba(0,0,0,0.3);border:1.5px solid rgba(245,158,11,0.45);border-radius:10px;padding:12px 14px;margin-bottom:12px;text-align:left;">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;border-bottom:1px solid rgba(244,234,212,0.1);padding-bottom:6px;flex-wrap:wrap;gap:8px;">
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="font-size:1.35rem;">🧩</span>
                  <div>
                    <div style="color:#facc15;font-size:0.96rem;font-weight:800;">Daily Puzzle Streaks &amp; Stats</div>
                    <div style="font-size:0.72rem;color:#9ca3af;">Hand of the Day · Discard &amp; Pegging Challenges</div>
                  </div>
                </div>
                <button type="button" class="btn-daily-puzzle-link" id="btnProfileModalPlayDaily" data-action="open-daily" style="display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:6px;font-size:0.78rem;font-weight:700;border:1px solid #f59e0b;background:rgba(245,158,11,0.22);color:#facc15;cursor:pointer;transition:transform 0.15s ease;">
                  ${isTodayDone ? '✅ Completed Today' : '▶️ Play Today’s Puzzle'}
                </button>
              </div>
              <div class="daily-puzzle-stats-grid" style="display:grid;grid-template-columns:repeat(4, 1fr);gap:6px;text-align:center;">
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Current Streak</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#f59e0b;margin-top:2px;">
                    ${dp.currentStreak > 0 ? `🔥 ${dp.currentStreak} Day${dp.currentStreak === 1 ? '' : 's'}` : '0 Days'}
                  </div>
                </div>
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Best Streak</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#facc15;margin-top:2px;">
                    ⭐ ${dp.bestStreak || 0} Day${dp.bestStreak === 1 ? '' : 's'}
                  </div>
                </div>
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Optimal Discards</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#10b981;margin-top:2px;">
                    🎯 ${dp.perfectDiscards || 0}
                  </div>
                </div>
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Pegging Won</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#38bdf8;margin-top:2px;">
                    ⚡ ${dp.peggingWins || 0}
                  </div>
                </div>
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-size:0.75rem;color:#9ca3af;padding-top:4px;border-top:1px solid rgba(244,234,212,0.06);">
                <span>Total Puzzles Played: <strong style="color:#fff;">${dp.totalPlayed || 0}</strong></span>
                <span>Last Played: <strong style="color:#e5e7eb;">${dp.lastPlayedDate || 'Never'}</strong></span>
              </div>
            </div>
          `;
        })() : ''}

        ${(activeTab === 'all' || activeTab === 'trainer') ? (() => {
          const dt = profile.getDiscardTrainerStats(context.storage);
          return `
            <!-- Global Discard Trainer Career Statistics Across All Brands -->
            <div class="profile-discard-trainer-card" style="background:rgba(0,0,0,0.3);border:1.5px solid rgba(16,185,129,0.45);border-radius:10px;padding:12px 14px;margin-bottom:12px;text-align:left;">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;border-bottom:1px solid rgba(244,234,212,0.1);padding-bottom:6px;flex-wrap:wrap;gap:8px;">
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="font-size:1.35rem;">🎯</span>
                  <div>
                    <div style="color:#34d399;font-size:0.96rem;font-weight:800;">Discard Trainer Career Stats</div>
                    <div style="font-size:0.72rem;color:#9ca3af;">Global Optimal Discards &amp; EV Accuracy across all 4 brands</div>
                  </div>
                </div>
                <button type="button" class="btn-trainer-play-link" id="btnProfileModalPracticeDiscards" data-action="open-trainer" style="display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:6px;font-size:0.78rem;font-weight:700;border:1px solid #10b981;background:rgba(16,185,129,0.22);color:#34d399;cursor:pointer;transition:transform 0.15s ease;">
                  🎯 Practice Discards
                </button>
              </div>
              <div class="discard-trainer-stats-grid" style="display:grid;grid-template-columns:repeat(4, 1fr);gap:6px;text-align:center;">
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Hands Practiced</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#facc15;margin-top:2px;">
                    ${dt.handsPlayed || 0}
                  </div>
                </div>
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Optimal Accuracy</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#10b981;margin-top:2px;">
                    ${dt.accuracyPct}% <span style="font-size:0.72rem;color:#9ca3af;">(${dt.optimalCount})</span>
                  </div>
                </div>
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Optimal Streak</div>
                  <div style="font-size:1.1rem;font-weight:800;color:#38bdf8;margin-top:2px;">
                    ${dt.currentStreak > 0 ? `🔥 ${dt.currentStreak}` : '0'} <span style="font-size:0.72rem;color:#9ca3af;">(Best: ${dt.bestStreak})</span>
                  </div>
                </div>
                <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;">
                  <div style="font-size:0.68rem;color:#9ca3af;font-weight:700;text-transform:uppercase;">Avg EV Loss</div>
                  <div style="font-size:1.1rem;font-weight:800;color:${dt.averageLossEV === 0 ? '#10b981' : '#f87171'};margin-top:2px;">
                    -${dt.averageLossEV.toFixed(2)}
                  </div>
                </div>
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-size:0.75rem;color:#9ca3af;padding-top:4px;border-top:1px solid rgba(244,234,212,0.06);">
                <span>Cumulative EV Loss: <strong style="color:#e5e7eb;">-${dt.totalLossEV.toFixed(2)}</strong></span>
                <span>Last Practiced: <strong style="color:#e5e7eb;">${dt.lastPlayedDate || 'Never'}</strong></span>
              </div>
            </div>
          `;
        })() : ''}

        ${(activeTab === 'all' || activeTab === 'boards') ? (() => {
          const boardsData = profile.getCustom3DBoards(context.storage);
          const totalCustom = boardsData?.list?.length || 0;
          return `
            <!-- Custom 3D Boards by Brand -->
            <div class="profile-custom-boards-card" style="background:rgba(0,0,0,0.3);border:1.5px solid rgba(56,189,248,0.45);border-radius:10px;padding:12px 14px;margin-bottom:12px;text-align:left;">
              <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;border-bottom:1px solid rgba(244,234,212,0.1);padding-bottom:6px;flex-wrap:wrap;gap:8px;">
                <div style="display:flex;align-items:center;gap:8px;">
                  <span style="font-size:1.35rem;">🎲</span>
                  <div>
                    <div style="color:#38bdf8;font-size:0.96rem;font-weight:800;">Custom 3D Boards Gallery</div>
                    <div style="font-size:0.72rem;color:#9ca3af;">User-configured 3D cribbage boards saved per brand in your profile</div>
                  </div>
                </div>
                <button type="button" class="btn-boards-3d-link" id="btnProfileModalOpen3DBoards" data-action="open-3d-boards" data-brand="all" style="display:inline-flex;align-items:center;gap:6px;padding:5px 12px;border-radius:6px;font-size:0.78rem;font-weight:700;border:1px solid #38bdf8;background:rgba(56,189,248,0.22);color:#38bdf8;cursor:pointer;transition:transform 0.15s ease;">
                  🎲 Open 3D Boards Modal
                </button>
              </div>
              <div class="custom-boards-grid" style="display:grid;grid-template-columns:repeat(4, 1fr);gap:6px;text-align:center;">
                ${GAME_BRANDS.map(b => {
                  const bCount = (boardsData?.byBrand?.[b.id] || []).length;
                  return `
                    <div style="background:rgba(255,255,255,0.03);border:1px solid rgba(244,234,212,0.08);border-radius:8px;padding:6px 4px;cursor:pointer;" data-action="open-3d-boards" data-brand="${b.id}" title="View ${b.name} 3D boards">
                      <div style="font-size:0.68rem;color:${b.color};font-weight:700;text-transform:uppercase;">${b.shortName}</div>
                      <div style="font-size:1.1rem;font-weight:800;color:#fff;margin-top:2px;">
                        ${bCount} Board${bCount === 1 ? '' : 's'}
                      </div>
                    </div>
                  `;
                }).join('')}
              </div>
              <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;font-size:0.75rem;color:#9ca3af;padding-top:4px;border-top:1px solid rgba(244,234,212,0.06);">
                <span>Total Custom Boards: <strong style="color:#fff;">${totalCustom}</strong></span>
                <span style="color:#38bdf8;cursor:pointer;" data-action="open-3d-boards" data-brand="all">Manage &amp; Add Custom Boards ➔</span>
              </div>
            </div>
          `;
        })() : ''}

        <!-- Recent Match History List -->
        <div style="text-align:left;">
          <div style="font-size:0.8rem;text-transform:uppercase;letter-spacing:0.06em;color:#f59e0b;font-weight:700;margin-bottom:6px;">
            ${activeTab === 'all' ? 'Recent Match History (All Games)' : (activeTab === 'daily' || activeTab === 'trainer' || activeTab === 'boards' ? 'Match History' : `Recent ${(GAME_BRANDS.find(b => b.id === activeTab)?.shortName) || ''} Matches`)}
          </div>
          <div style="background:rgba(0,0,0,0.25);border:1px solid rgba(244,234,212,0.18);border-radius:8px;max-height:28vh;overflow-y:auto;">
            ${historyRowsHtml}
          </div>
        </div>

        <!-- Actions -->
        <div style="margin-top:16px;display:flex;justify-content:center;gap:10px;flex-wrap:wrap;">
          <button type="button" class="btn-gold" id="btnProfileModalChangeAvatarAction" data-action="open-avatar" style="cursor:pointer;display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:8px;font-weight:700;">
            🎭 Change Avatar
          </button>
          <button type="button" class="btn-gold" id="btnProfileModalEditNameAction" data-action="edit-name" style="cursor:pointer;display:inline-flex;align-items:center;gap:6px;padding:8px 14px;border-radius:8px;font-weight:700;">
            ✏️ Edit Username
          </button>
          <button type="button" class="btn-danger" id="btnResetProfileStats" data-action="reset-stats" style="padding:8px 14px;border-radius:8px;font-weight:700;cursor:pointer;">
            Reset Statistics
          </button>
          <button type="button" class="btn-ghost modal-btn-close" id="btnCloseRecords" data-action="close" style="padding:8px 14px;border-radius:8px;font-weight:700;cursor:pointer;">
            Close
          </button>
        </div>
      </div>
    `;

    const showModalFn = context.showModal || (typeof window !== 'undefined' && window.showModal ? window.showModal : null);
    if (typeof showModalFn === 'function') {
      showModalFn(modalHtml, true);
      const root = (typeof document !== 'undefined' && typeof document.querySelector === 'function')
        ? (document.querySelector('.profile-records-modal-content') || (typeof document.getElementById === 'function' ? document.getElementById('overlay') : null))
        : null;
      if (root) Profile.bindModalEvents(root);
    }
  }

  /**
   * Centralized event delegation binder for Profile records modal.
   * @param {HTMLElement} root
   */
  static bindModalEvents(root) {
    if (!root || typeof root.addEventListener !== 'function') return;

    root.addEventListener('click', (e) => {
      const target = e.target.closest('[data-action]');
      if (!target) return;

      const action = target.dataset.action;
      switch (action) {
        case 'view-trophies': {
          const brandId = target.dataset.brand || 'all';
          if (typeof window !== 'undefined') {
            if (window.showTrophyRoomModal) window.showTrophyRoomModal(brandId);
            else if (window.AchievementManager?.showTrophyRoomModal) window.AchievementManager.showTrophyRoomModal(brandId);
          }
          break;
        }
        case 'view-replays': {
          const brandId = target.dataset.brand || 'all';
          if (typeof window !== 'undefined') {
            if (window.showReplaysModal) window.showReplaysModal(brandId);
            else if (window.ReplayModal?.openLibrary) window.ReplayModal.openLibrary(brandId);
          }
          break;
        }
        case 'open-3d-boards': {
          const brandId = target.dataset.brand || 'all';
          Profile.openBoard3DModal(brandId);
          break;
        }
        case 'open-avatar':
          Profile.openAvatarSelector();
          break;
        case 'edit-name':
          Profile.startInlineNameEdit();
          break;
        case 'save-inline-name':
          Profile.saveInlineName();
          break;
        case 'cancel-inline-name':
          Profile.cancelInlineNameEdit();
          break;
        case 'open-trainer':
          Profile.openDiscardTrainer();
          break;
        case 'open-daily':
          Profile.openDailyPuzzle();
          break;
        case 'switch-tab': {
          const tab = target.dataset.tab;
          if (tab) Profile.switchRecordsTab(tab);
          break;
        }
        case 'reset-stats':
          Profile.resetStatsConfirm();
          break;
        case 'close':
          if (typeof hideModal === 'function') hideModal();
          else if (typeof window !== 'undefined' && typeof window.hideModal === 'function') window.hideModal();
          break;
      }
    });

    const sel = root.querySelector('#selectProfileRecordsTab');
    if (sel) {
      sel.addEventListener('change', () => {
        Profile.switchRecordsTab(sel.value);
      });
    }

    const inlineInput = root.querySelector('#inputProfileInlineName');
    if (inlineInput) {
      inlineInput.addEventListener('input', () => {
        inlineInput.value = inlineInput.value.replace(/[^a-zA-Z0-9]/g, '');
      });
      inlineInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') Profile.saveInlineName();
        if (e.key === 'Escape') Profile.cancelInlineNameEdit();
      });
    }
  }

  /**
   * Opens the avatar selection modal from the profile modal.
   */
  static openAvatarSelector() {
    const avMgr = (typeof window !== 'undefined' && window.AvatarManager)
      || (typeof AvatarManager !== 'undefined' ? AvatarManager : null)
      || (typeof window !== 'undefined' && window.CrazyCribbage && window.CrazyCribbage.AvatarManager);
    if (avMgr && typeof avMgr.openAvatarModal === 'function') {
      avMgr.openAvatarModal({
        onSelect: (newAvatarId) => {
          const profile = Profile.getInstance(Profile._lastContext?.storage);
          profile.setAvatar(newAvatarId, Profile._lastContext?.storage);
          if (typeof window !== 'undefined' && typeof window.updateProfileDropdownUI === 'function') {
            window.updateProfileDropdownUI();
          }
          Profile.showRecordsModal(Profile._lastContext || {});
        }
      });
    }
  }

  /**
   * Activates inline editing for username in the profile header banner.
   */
  static startInlineNameEdit() {
    if (typeof document === 'undefined') return;
    const viewRow = document.getElementById('profileHeaderNameRow');
    const editRow = document.getElementById('profileHeaderNameEditRow');
    const input = document.getElementById('inputProfileInlineName');
    if (viewRow && editRow) {
      viewRow.style.display = 'none';
      editRow.style.display = 'flex';
      if (input) {
        input.focus();
        input.select();
        input.oninput = () => {
          input.value = input.value.replace(/[^a-zA-Z0-9]/g, '');
        };
        input.onkeydown = (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            Profile.saveInlineName();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            Profile.cancelInlineNameEdit();
          }
        };
      }
    }
  }

  /**
   * Cancels inline username editing and restores original name.
   */
  static cancelInlineNameEdit() {
    if (typeof document === 'undefined') return;
    const viewRow = document.getElementById('profileHeaderNameRow');
    const editRow = document.getElementById('profileHeaderNameEditRow');
    const input = document.getElementById('inputProfileInlineName');
    const profile = Profile.getInstance(Profile._lastContext?.storage);
    if (input) {
      input.value = profile.getName(Profile._lastContext?.storage);
    }
    if (viewRow && editRow) {
      editRow.style.display = 'none';
      viewRow.style.display = 'flex';
    }
  }

  /**
   * Saves new username from inline edit input, updates DOM & storage, and returns to view mode.
   */
  static saveInlineName() {
    if (typeof document === 'undefined') return;
    const input = document.getElementById('inputProfileInlineName');
    if (!input) return;
    const sanitized = sanitizeUserInput(input.value);
    const newName = (sanitized.replace(/[^a-zA-Z0-9]/g, '').trim()) || 'Player';
    const profile = Profile.getInstance(Profile._lastContext?.storage);
    profile.setName(newName, Profile._lastContext?.storage);

    const nameDisplay = document.getElementById('profileHeaderNameDisplay');
    if (nameDisplay) nameDisplay.textContent = newName;

    if (typeof window !== 'undefined' && typeof window.updateProfileDropdownUI === 'function') {
      window.updateProfileDropdownUI();
    }

    const viewRow = document.getElementById('profileHeaderNameRow');
    const editRow = document.getElementById('profileHeaderNameEditRow');
    if (viewRow && editRow) {
      editRow.style.display = 'none';
      viewRow.style.display = 'flex';
    }
  }

  /**
   * Prompts user with confirmation before resetting statistics.
   * Supports resetting a specific game or all 4 games.
   * @param {string} [brand=null]
   */
  static resetStatsConfirm(brand = null) {
    const targetBrand = brand || Profile._selectedRecordsTab || 'all';
    const showConfirmModalFn = (typeof window !== 'undefined' && window.showConfirmModal ? window.showConfirmModal : null);
    const brandObj = GAME_BRANDS.find(b => b.id === targetBrand);
    let title;
    let message;

    if (targetBrand === 'daily' || targetBrand === 'dailyPuzzle') {
      title = 'Reset Daily Puzzle Statistics & Streaks?';
      message = 'This will reset your Daily Puzzle current streak, best streak, optimal discards, and pegging results to zero. Player profile and game match records will remain intact.';
    } else if (targetBrand === 'trainer' || targetBrand === 'discardTrainer') {
      title = 'Reset Discard Trainer Statistics?';
      message = 'This will reset your Discard Trainer career hands played, optimal discards, streaks, and EV accuracy to zero across all brands. Player profile and game match records will remain intact.';
    } else if (targetBrand === 'pegging' || targetBrand === 'peggingTrainer') {
      title = 'Reset Pegging Tactics Trainer Statistics?';
      message = 'This will reset your Pegging Trainer career scenarios played, optimal moves, streaks, and accuracy to zero across all brands. Player profile and game match records will remain intact.';
    } else if (targetBrand === 'all') {
      title = 'Reset All Match Statistics Across All 4 Games?';
      message = 'This will reset your match history, win rate, and streak records to zero across Crazy Cribbage, Cribbage Clash, Pints & Pegs, and Skunked & Stripped. Player name, avatar, Discard Trainer, and Daily Puzzle streaks will remain intact.';
    } else {
      title = `Reset All Statistics for ${brandObj?.name || targetBrand}?`;
      message = `This will reset your match history and win rate for ${brandObj?.name || targetBrand} to zero. Player name, avatar, Discard Trainer, and Daily Puzzle streaks will remain intact.`;
    }

    const onConfirm = () => {
      const profile = Profile.getInstance(Profile._lastContext?.storage);
      profile.clearRecords(Profile._lastContext?.storage, targetBrand);
      Profile.showRecordsModal(Profile._lastContext || {});
    };

    if (typeof showConfirmModalFn === 'function') {
      showConfirmModalFn({
        title,
        message,
        confirmLabel: 'Yes, Reset',
        cancelLabel: 'Cancel',
        onConfirm
      });
    } else {
      if (typeof window !== 'undefined' && window.confirm && window.confirm(title)) {
        onConfirm();
      }
    }
  }

  /**
   * Opens interactive modal / prompt allowing the user to change their username.
   */
  static promptChangeUsername() {
    const profile = Profile.getInstance();
    const currentName = profile.getName();

    const modalHtml = `
      <div style="text-align:left;max-width:400px;margin:0 auto;">
        <h2 style="margin-top:0;color:#f59e0b;font-size:1.3rem;">Change Username</h2>
        <p style="color:#d1d5db;font-size:0.88rem;margin-bottom:14px;">
          Choose your player name for solo matches, scoreboard chips, and online multiplayer rooms:
        </p>
        <div style="margin-bottom:16px;">
          <input type="text" id="inputProfileNewUsername" value="${currentName.replace(/"/g, '&quot;')}" maxlength="24" placeholder="Enter username" pattern="[a-zA-Z0-9]+" style="width:100%;box-sizing:border-box;padding:10px 14px;border-radius:8px;border:1.5px solid rgba(245,158,11,0.5);background:#0f172a;color:#fff;font-size:1rem;font-weight:600;outline:none;" />
        </div>
        <div style="display:flex;justify-content:flex-end;gap:10px;">
          <button type="button" class="btn-ghost" id="btnCancelNewUsername">Cancel</button>
          <button type="button" class="btn-gold" id="btnSaveNewUsername">Save Username</button>
        </div>
      </div>
    `;

    if (typeof window !== 'undefined' && typeof window.showModal === 'function') {
      window.showModal(modalHtml, false);
      setTimeout(() => {
        const inp = document.getElementById('inputProfileNewUsername');
        if (inp) {
          inp.focus();
          inp.select();
          inp.addEventListener('input', () => {
            inp.value = inp.value.replace(/[^a-zA-Z0-9]/g, '');
          });
          inp.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
              Profile.saveUsernameFromInput();
            }
          });
        }
        const btnSave = document.getElementById('btnSaveNewUsername');
        if (btnSave) {
          btnSave.addEventListener('click', () => Profile.saveUsernameFromInput());
        }
        const btnCancel = document.getElementById('btnCancelNewUsername');
        if (btnCancel) {
          btnCancel.addEventListener('click', () => {
            if (typeof hideModal === 'function') hideModal();
            else if (typeof window !== 'undefined' && typeof window.hideModal === 'function') window.hideModal();
          });
        }
      }, 50);
    }
  }

  /**
   * Saves new username from the modal input and closes modal.
   */
  static saveUsernameFromInput() {
    if (typeof document === 'undefined') return;
    const inp = document.getElementById('inputProfileNewUsername');
    if (!inp) return;
    const sanitized = sanitizeUserInput(inp.value);
    const newName = (sanitized.replace(/[^a-zA-Z0-9]/g, '').trim()) || 'Player';
    const profile = Profile.getInstance(Profile._lastContext?.storage);
    profile.setName(newName, Profile._lastContext?.storage);
    if (typeof window !== 'undefined') {
      if (typeof window.updateProfileDropdownUI === 'function') {
        window.updateProfileDropdownUI();
      }
      if (Profile._wasRecordsModalOpen) {
        Profile.showRecordsModal(Profile._lastContext || {});
        Profile._wasRecordsModalOpen = false;
        return;
      }
      if (typeof window.hideModal === 'function') {
        window.hideModal();
      }
    }
  }
}

if (typeof window !== 'undefined') {
  window.Profile = Profile;
}
